import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { equipmentAPI, settingsAPI, commercialConfigAPI, aiAPI, cleaningConfigAPI } from '../services/api';
import { withReportProfile, unitReportAddress } from '../utils/reportProfiles';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import useApprovalRights from '../hooks/useApprovalRights';
import { toast } from 'react-toastify';
import { ActionsMenu } from '../components/ActionsMenu';
import { FaPlus, FaEdit, FaTrash, FaEye, FaDownload, FaUpload, FaSearch, FaCopy, FaSave, FaCheck, FaTools, FaLink, FaBars, FaLayerGroup, FaPrint, FaHourglassHalf, FaTimes } from 'react-icons/fa';
import { StatCard } from '../components/UnifiedUIComponents';
import { ESignModal, Modal, ModalFooter } from '../components/Modals';
import VersionHistoryModal from '../components/VersionHistoryModal';
import ChangeReferenceModal from '../components/ChangeReferenceModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import { openPrintPreview, getActivePrintUnitName } from '../utils/printUtils';
import { matchesPlantScope } from '../utils/helpers';
import TablePagination from '../components/TablePagination';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const TRAIN_STATUS = {
  DRAFT: 'Draft',
  PENDING: 'Pending Approval',
  APPROVED: 'Approved'
};
const TRAIN_AREA_COMPENSATION_SETTING_KEY = 'train_area_compensation_percent';

const createTrainMember = (overrides = {}) => ({
  equipment_id: '',
  equipment_code: '',
  equipment_name: '',
  production_line: '',
  sequence_order: 1,
  area_scope: 'All',
  cleaning_method: 'Manual',
  notes: '',
  usage_remark: '',
  validation_batch_justification: '',
  manual_contamination_required: false,
  manual_contamination_notes: '',
  maco_value: '',
  contamination_factor: '',
  product_quantity: '',
  contamination_quantity: '',
  attachedFiles: [],
  ...overrides
});

const EMPTY_TRAIN_FORM = {
  train_code: '',
  train_name: '',
  production_line: '',
  description: '',
  notes: '',
  status: TRAIN_STATUS.DRAFT,
  members: [createTrainMember()],
  attachedFiles: []
};

const toNum = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeStatus = (status) => {
  if (!status) return TRAIN_STATUS.DRAFT;
  const lowered = String(status).trim().toLowerCase();
  if (lowered === 'pending approval' || lowered === 'pending') return TRAIN_STATUS.PENDING;
  if (lowered === 'approved' || lowered === 'active') return TRAIN_STATUS.APPROVED;
  if (lowered === 'returned') return TRAIN_STATUS.RETURNED;
  if (lowered === 'rejected') return TRAIN_STATUS.REJECTED;
  if (lowered === 'edit required') return TRAIN_STATUS.EDIT_REQUIRED;
  return TRAIN_STATUS.DRAFT;
};

const APPROVAL_DECISION_OPTIONS = [
  { value: 'approve', label: 'Approve' },
  { value: 'return', label: 'Return' },
  { value: 'reject', label: 'Reject' },
  { value: 'edit_required', label: 'Edit Required' }
];

const getTrainDeleteStage = (train) => {
  const displayStatus = normalizeStatus(train?.status);
  if (displayStatus === TRAIN_STATUS.DRAFT) return 'draft';
  if (!train?.deletion_requested) return 'request';
  if (!train?.deletion_product_approved) return 'product';
  return 'qa';
};

const getTrainStatusInfo = (train) => {
  const displayStatus = normalizeStatus(train?.status);
  if (train?.deletion_requested) {
    if (!train?.deletion_product_approved) {
      return { text: 'Pending Product Deletion Approval', badgeClass: 'badge-warning' };
    }
    if (!train?.deletion_qa_approved) {
      return { text: 'Pending QA Deletion Approval', badgeClass: 'badge-warning' };
    }
  }
  if (displayStatus === TRAIN_STATUS.DRAFT) return { text: displayStatus, badgeClass: 'badge-warning' };
  if (displayStatus === TRAIN_STATUS.PENDING) return { text: displayStatus, badgeClass: 'badge-info' };
  return { text: displayStatus, badgeClass: 'badge-success' };
};

const normalizeAreaScope = (value) => {
  const lowered = String(value || '').trim().toLowerCase();
  if (lowered === 'direct') return 'Direct';
  if (lowered === 'indirect') return 'Indirect';
  return 'All';
};

const normalizeCleaningMethod = (value) => {
  const lowered = String(value || '').trim().toLowerCase();
  if (lowered === 'cip') return 'CIP';
  if (lowered === 'cop') return 'COP';
  if (lowered === 'automated') return 'Automated';
  return 'Manual';
};

const toNullableInputNumber = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCalculatedContaminationQuantity = (member) => {
  const macoValue = toNullableInputNumber(member?.maco_value);
  const contaminationFactor = toNullableInputNumber(member?.contamination_factor);
  const productQuantity = toNullableInputNumber(member?.product_quantity);
  if (![macoValue, contaminationFactor, productQuantity].every((value) => Number.isFinite(value))) return null;
  return Number((macoValue * contaminationFactor * productQuantity).toFixed(6));
};

const formatContaminationDisplay = (member) => {
  const contaminationQuantity = toNullableInputNumber(member?.contamination_quantity) ?? getCalculatedContaminationQuantity(member);
  if (!Number.isFinite(contaminationQuantity)) return '';
  const macoValue = toNullableInputNumber(member?.maco_value);
  const contaminationFactor = toNullableInputNumber(member?.contamination_factor);
  const productQuantity = toNullableInputNumber(member?.product_quantity);
  const parts = ['Contamination qty: ' + contaminationQuantity.toFixed(6)];
  if (Number.isFinite(macoValue)) parts.push('MACO ' + macoValue);
  if (Number.isFinite(contaminationFactor)) parts.push('Factor ' + contaminationFactor);
  if (Number.isFinite(productQuantity)) parts.push('Product qty ' + productQuantity);
  return parts.join(' | ');
};

const getMissingEquipmentMasterFields = (equipment) => {
  if (!equipment) return ['equipment record'];
  const missing = [];
  if (!String(equipment.equipment_code || '').trim()) missing.push('equipment code');
  if (!String(equipment.equipment_name || '').trim()) missing.push('equipment name');
  if (!String(equipment.equipment_type || '').trim()) missing.push('equipment type');
  if (!String(equipment.production_line || '').trim()) missing.push('production line');

  return missing;
};

const parseFiles = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [value];
    }
  }
  return [];
};

const toFileUrl = (file) => {
  if (!file) return '';
  if (typeof file === 'string' && /^https?:\/\//i.test(file)) return file;
  return `${API_ORIGIN}/uploads/${file}`;
};

const toDataUrlIfPossible = (src) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }
  const trimmed = String(src).trim();
  if (!trimmed) {
    resolve(null);
    return;
  }
  if (trimmed.startsWith('data:image/')) {
    resolve(trimmed);
    return;
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = trimmed;
});

const FileUploader = ({ label, files, onAdd, onRemove, id }) => {
  const fileList = Array.isArray(files) ? files : (files ? [files] : []);
  const [pendingAttachmentName, setPendingAttachmentName] = useState('');
  const getFileDisplayName = (file) => {
    if (file?.name) return file.name;
    const raw = String(file || '');
    const chunks = raw.split(/[\\/]/);
    return chunks[chunks.length - 1] || raw || 'Attachment';
  };

  const handleFileClick = (file) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else if (typeof file === 'string') {
      window.open(toFileUrl(file), '_blank');
    }
  };

  const buildNamedFiles = (selectedFiles) => {
    const custom = String(pendingAttachmentName || '').trim();
    if (!custom) return selectedFiles;
    return selectedFiles.map((file, index) => {
      const originalName = String(file?.name || '');
      const extMatch = originalName.match(/(\.[^./\\]+)$/);
      const ext = extMatch ? extMatch[1] : '';
      const customHasExt = /\.[^./\\]+$/.test(custom);
      const baseName = customHasExt ? custom : `${custom}${ext}`;
      const finalName = selectedFiles.length > 1
        ? baseName.replace(/(\.[^./\\]+)?$/, (_, foundExt = '') => `_${index + 1}${foundExt}`)
        : baseName;
      return new File([file], finalName, { type: file.type, lastModified: file.lastModified });
    });
  };

  return (
    <div className="form-group" style={{ minWidth: '0' }}>
      <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)', marginBottom: '4px', display: 'block' }}>{label}</label>
      <div style={{ border: '1px dashed var(--border-color)', borderRadius: '6px', padding: '8px', background: 'var(--card-bg)', minHeight: '38px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: fileList.length > 0 ? '8px' : '0' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{fileList.length > 0 ? `${fileList.length} file(s)` : 'No files'}</span>
          <input
            type="text"
            className="form-control"
            placeholder="Attachment name (optional)"
            value={pendingAttachmentName}
            onChange={(e) => setPendingAttachmentName(e.target.value)}
            style={{ maxWidth: '210px', height: '28px', fontSize: 'var(--fs-sm)', padding: '4px 8px' }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => document.getElementById(id).click()}
            style={{ padding: '2px 8px', fontSize: 'var(--fs-sm)', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--primary-light)', background: 'var(--card-bg)' }}
          >
            <FaPlus style={{ fontSize: 'var(--fs-xs)' }} /> Add
          </button>
        </div>
        <input
          type="file"
          multiple
          id={id}
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              const selectedFiles = Array.from(e.target.files);
              onAdd(buildNamedFiles(selectedFiles));
              setPendingAttachmentName('');
              e.target.value = '';
            }
          }}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        />
        {fileList.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {fileList.map((file, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                  <span style={{ fontSize: 'var(--fs-md)' }}>[File]</span>
                  <span 
                    style={{ 
                      fontSize: 'var(--fs-sm)', 
                      color: 'var(--primary-color, #2563eb)', 
                      whiteSpace: 'nowrap', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      maxWidth: '100px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }} 
                    title={getFileDisplayName(file)}
                    onClick={() => handleFileClick(file)}
                  >
                    {getFileDisplayName(file)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  style={{ border: 'none', background: 'none', color: 'var(--danger-color)', cursor: 'pointer', padding: '2px', display: 'flex' }}
                  title="Remove"
                >
                  <FaTrash style={{ fontSize: 'var(--fs-xs)' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const formatTrainMember = (row) => ({
  train_member_id: row.train_member_id,
  equipment_id: row.equipment_id,
  equipment_code: row.equipment_code,
  equipment_name: row.equipment_name,
  production_line: row.production_line || '',
  sequence_order: toNum(row.sequence_order, 1),
  area_scope: normalizeAreaScope(row.area_scope),
  cleaning_method: normalizeCleaningMethod(row.cleaning_method),
  notes: row.notes || '',
  usage_remark: row.usage_remark || '',
  validation_batch_justification: row.validation_batch_justification || '',
  manual_contamination_required: Boolean(row.manual_contamination_required),
  manual_contamination_notes: row.manual_contamination_notes || '',
  maco_value: row.maco_value ?? '',
  contamination_factor: row.contamination_factor ?? '',
  product_quantity: row.product_quantity ?? '',
  contamination_quantity: row.contamination_quantity ?? getCalculatedContaminationQuantity(row) ?? '',
  direct_area: toNum(row.direct_area, 0),
  indirect_area: toNum(row.indirect_area, 0),
  total_area: toNum(row.total_area, 0),
  selected_area: toNum(row.selected_area, 0),
  attachedFiles: parseFiles(row.attached_files || row.attachedFiles)
});

const EquipmentTrain = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trains, setTrains] = useState([]);
  const [selectedTrains, setSelectedTrains] = useState([]);
  const grouping = useAIAction();
  const [equipmentList, setEquipmentList] = useState([]);
  const [membersCache, setMembersCache] = useState({});
  const [commercialLines, setCommercialLines] = useState([]);
  // Cleaning method/location is owned by Cleaning Config (per equipment / sub-part), not the train.
  const [cleaningConfigs, setCleaningConfigs] = useState([]);
  useEffect(() => {
    cleaningConfigAPI.getAll()
      .then((res) => setCleaningConfigs(res?.data?.data || []))
      .catch(() => setCleaningConfigs([]));
  }, []);
  const formatCleaningConfigSummary = (equipmentId) => {
    const rows = cleaningConfigs.filter((c) => String(c.equipment_id) === String(equipmentId) && c.cleaning_method);
    if (!rows.length) return 'Not configured in Cleaning Config';
    const parts = rows.map((c) => {
      const where = [c.cleaning_location, c.cleaning_area_grade ? `Grade ${c.cleaning_area_grade}` : ''].filter(Boolean).join(', ');
      return `${c.part_name ? `${c.part_name}: ` : ''}${c.cleaning_method}${where ? ` (${where})` : ''}`;
    });
    return [...new Set(parts)].join('; ');
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [trainNameFilter, setTrainNameFilter] = useState('All');
  const [productionLineFilter, setProductionLineFilter] = useState(localStorage.getItem('activeLine') || 'All');
  const [activePlantFilter, setActivePlantFilter] = useState(localStorage.getItem('activePlant') || 'All');
  const [sortBy, setSortBy] = useState('train_code');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusCard, setStatusCard] = useState('All'); // All | Approved | Pending | Draft
  const [trainAreaCompensationPercent, setTrainAreaCompensationPercent] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [selectedTrainRow, setSelectedTrainRow] = useState(null);
  const [showTrainModal, setShowTrainModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [trainDeletionImpact, setTrainDeletionImpact] = useState(null);
  const [editingTrainId, setEditingTrainId] = useState(null);
  const [qmsLinked, setQmsLinked] = useState(false);
  const [changeRefGate, setChangeRefGate] = useState(null); // {train} while the gate is open for an Approved row
  const [changeRefBusy, setChangeRefBusy] = useState(false);
  const [pendingChangeRef, setPendingChangeRef] = useState(null);
  const editingApprovedTrainRef = useRef(false);

  useEffect(() => {
    commercialConfigAPI.getRuntime().then((res) => {
      const integrations = res?.data?.data?.integrations || {};
      setQmsLinked(!!integrations.aiventra_qms?.features?.change_control_link);
    }).catch(() => {});
  }, []);
  const [historyModal, setHistoryModal] = useState(null); // train row
  const [viewTrain, setViewTrain] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const [trainFormData, setTrainFormData] = useState({ ...EMPTY_TRAIN_FORM });
  const [eSignData, setESignData] = useState({ password: '', comment: '', decision: 'approve' });
  const [localEquipmentSearchTerm, setLocalEquipmentSearchTerm] = useState('');
  const [equipmentSearchTerms, setEquipmentSearchTerms] = useState({});
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null);
  const [expandedTrainProducts, setExpandedTrainProducts] = useState(new Set());
  const [trainProducts, setTrainProducts] = useState({});
  const [loadingProducts, setLoadingProducts] = useState(new Set());
  const [reportSettings, setReportSettings] = useState({
    report_client_name: 'Client Name',
    report_client_address: 'Client Address',
    report_document_name: 'Equipment Train Report',
    report_document_rev_no: '01',
    report_client_logo: '',
    report_primeclean_logo: '',
    report_footer_tagline: 'Data is generated and handled by PrimeClean.',
    report_format_no_equipment_train: ''
  });
  const didInitialLoad = useRef(false);
  const initialTrainRef = useRef(null);
  // Snapshot the form the moment the modal opens so unchanged edits can skip the e-signature.
  useEffect(() => {
    initialTrainRef.current = showTrainModal
      ? { json: JSON.stringify(trainFormData), status: trainFormData.status }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrainModal]);
  const mobileImportInputRef = useRef(null);

  const role = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')?.role || '';
    } catch {
      return '';
    }
  })();
  const roleLower = String(role).trim().toLowerCase();
  const isSuperAdmin = roleLower === 'super admin';
  const isAdminLike = ['admin', 'super admin'].includes(roleLower);
  // Who may approve equipment trains: Settings → Approval Flow (Equipment Train), not a hardcoded role list.
  const { canEarlier: canTrainEarlierApprove, canFinal: canTrainFinalApprove } = useApprovalRights('equipment_train.approval');
  const canBulkTrainApprove = canTrainEarlierApprove || canTrainFinalApprove;
  const canUseBulkTrainActions = isSuperAdmin || canBulkTrainApprove;
  const selectedTrainIdSet = new Set(selectedTrains.map((id) => String(id)));
  const selectedTrainRecords = trains.filter((train) => selectedTrainIdSet.has(String(train.train_id)));
  const bulkTrainApproveEligible = selectedTrainRecords.filter((train) => normalizeStatus(train.status) === TRAIN_STATUS.PENDING && !train.deletion_requested);
  const bulkTrainDeleteProductEligible = selectedTrainRecords.filter((train) => train.deletion_requested && !train.deletion_product_approved);
  const bulkTrainDeleteQaEligible = selectedTrainRecords.filter((train) => train.deletion_requested && !train.deletion_qa_approved && (train.deletion_product_approved || isSuperAdmin));
  const pendingProductLink = location.state?.linkProduct || null;
  const pendingProductTrainLabel = pendingProductLink ? [pendingProductLink.train_code, pendingProductLink.train_name].filter(Boolean).join(' - ') : '';
  const selectedTrainForProductLink = pendingProductLink
    ? (trains.find((train) => String(train.train_id) === String(selectedTrainRow)) || null)
    : null;

  const normalizeLoadErrorMessage = (error, fallback) => {
    const message = String(error?.response?.data?.message || error?.message || '').trim().toLowerCase();
    if (message === 'authentication error' || message === 'failed to validate session') {
      return `${fallback} (server validation issue)`;
    }
    return fallback;
  };

  const extractTrainRows = (payload) => {
    if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const extractFullTrainData = (payload) => {
    if (payload?.data?.data && typeof payload.data.data === 'object') return payload.data.data;
    if (payload?.data && typeof payload.data === 'object') return payload.data;
    return null;
  };

  const buildTrainFormMembers = (members = []) => (
    members.length > 0
      ? members.map((m) => createTrainMember({
          train_member_id: m.train_member_id,
          equipment_id: m.equipment_id,
          equipment_code: m.equipment_code || '',
          equipment_name: m.equipment_name || '',
          production_line: m.production_line || '',
          sequence_order: m.sequence_order,
          area_scope: normalizeAreaScope(m.area_scope),
          cleaning_method: normalizeCleaningMethod(m.cleaning_method),
          notes: m.notes || '',
          usage_remark: m.usage_remark || '',
          validation_batch_justification: m.validation_batch_justification || '',
          manual_contamination_required: Boolean(m.manual_contamination_required),
          manual_contamination_notes: m.manual_contamination_notes || '',
          maco_value: m.maco_value ?? '',
          contamination_factor: m.contamination_factor ?? '',
          product_quantity: m.product_quantity ?? '',
          contamination_quantity: m.contamination_quantity ?? getCalculatedContaminationQuantity(m) ?? '',
          attachedFiles: parseFiles(m.attached_files || m.attachedFiles)
        }))
      : [createTrainMember()]
  );

  const getNextTrainCode = () => {
    const maxNum = (trains || []).reduce((max, train) => {
      const code = String(train?.train_code || '').toUpperCase();
      const match = code.match(/^TRN-(\d+)$/);
      if (!match) return max;
      const value = Number.parseInt(match[1], 10);
      return Number.isFinite(value) ? Math.max(max, value) : max;
    }, 0);
    const next = maxNum + 1;
    return `TRN-${String(next).padStart(3, '0')}`;
  };

  const resetForm = () => {
    setEditingTrainId(null);
    setTrainFormData({ ...EMPTY_TRAIN_FORM, train_code: getNextTrainCode() });
    setEquipmentSearchTerms({});
    editingApprovedTrainRef.current = false;
    setPendingChangeRef(null);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      commercialConfigAPI.getFull().then((res) => {
        setCommercialLines(res?.data?.data?.lines || []);
      }).catch(() => {});
      const [trainsRes, equipmentRes, settingsRes] = await Promise.allSettled([
        equipmentAPI.getTrainsInline(),
        equipmentAPI.getAll({ limit: 1000 }),
        settingsAPI.getAll()
      ]);
      
      let trainRows = [];
      if (trainsRes.status === 'fulfilled') {
        trainRows = extractTrainRows(trainsRes.value).map((row) => ({
          ...row,
          status: normalizeStatus(row.status)
        }));
      } else {
        console.warn(normalizeLoadErrorMessage(trainsRes.reason, 'Failed to load train list'));
      }
      const equipmentPayload = equipmentRes.status === 'fulfilled' ? equipmentRes.value : null;
      const settingsPayload = settingsRes.status === 'fulfilled' ? settingsRes.value : null;
      let eqRows = []; // Get all equipment for dropdown/modal filtering
      if (Array.isArray(equipmentPayload?.data)) eqRows = equipmentPayload.data;
      else if (Array.isArray(equipmentPayload?.data?.data?.rows)) eqRows = equipmentPayload.data.data.rows;
      else if (Array.isArray(equipmentPayload?.data?.data)) eqRows = equipmentPayload.data.data;

      setTrains(trainRows);
      setEquipmentList(eqRows);
      await Promise.allSettled(trainRows.map((train) => loadMembers(train.train_id, true)));

      const settings = settingsPayload?.data?.data || {};
      setReportSettings(prev => ({
        ...prev,
        report_client_name: settings.report_client_name || prev.report_client_name,
        report_client_address: settings.report_client_address || prev.report_client_address,
        report_document_name: settings.report_document_name || 'Equipment Train Report',
        report_document_rev_no: settings.report_document_rev_no || prev.report_document_rev_no,
        report_client_logo: settings.report_client_logo || prev.report_client_logo,
        report_primeclean_logo: settings.report_primeclean_logo || prev.report_primeclean_logo,
        report_format_no_equipment_train: settings.report_format_no_equipment_train || prev.report_format_no_equipment_train
      }));
      setTrainAreaCompensationPercent(Math.max(0, Number(settings[TRAIN_AREA_COMPENSATION_SETTING_KEY]) || 0));
    } catch (error) {
      console.warn(normalizeLoadErrorMessage(error, 'Failed to load train data'));
    } finally {
      setLoading(false);
    }
  };

  const extractMemberRows = (payload) => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const loadMembers = async (trainId, force = false) => {
    if (!trainId) return [];
    if (!force && membersCache[trainId]) return membersCache[trainId];

    try {
      const response = await equipmentAPI.getTrainMembers(trainId);
      const rows = extractMemberRows(response).map(formatTrainMember);
      setMembersCache((prev) => ({ ...prev, [trainId]: rows }));
      return rows;
    } catch (error) {
      toast.error(normalizeLoadErrorMessage(error, 'Failed to load train members'));
      setMembersCache((prev) => ({ ...prev, [trainId]: [] }));
      return [];
    }
  };

  const loadTrainProducts = async (trainId) => {
    if (!trainId || trainProducts[trainId]) return trainProducts[trainId];
    
    try {
      setLoadingProducts(prev => new Set([...prev, trainId]));
      const response = await equipmentAPI.getTrainFull(trainId);
      const linkedProducts = response?.data?.data?.linked_products || response?.data?.linked_products || [];
      setTrainProducts(prev => ({ ...prev, [trainId]: linkedProducts }));
      return linkedProducts;
    } catch (error) {
      console.error('Failed to load train products:', error);
      setTrainProducts(prev => ({ ...prev, [trainId]: [] }));
      return [];
    } finally {
      setLoadingProducts(prev => {
        const next = new Set(prev);
        next.delete(trainId);
        return next;
      });
    }
  };

  const toggleTrainProducts = async (trainId) => {
    const isExpanded = expandedTrainProducts.has(trainId);
    if (isExpanded) {
      setExpandedTrainProducts(prev => {
        const next = new Set(prev);
        next.delete(trainId);
        return next;
      });
    } else {
      setExpandedTrainProducts(prev => new Set([...prev, trainId]));
      await loadTrainProducts(trainId);
    }
  };

  const resolveTrainForRoute = async (trainId) => {
    const requestedTrainId = String(trainId || '').trim();
    if (!requestedTrainId) return null;

    const selectedTrain = trains.find((t) => String(t.train_id) === requestedTrainId);
    if (selectedTrain) {
      const members = await loadMembers(selectedTrain.train_id, true);
      return { train: selectedTrain, members };
    }

    try {
      const response = await equipmentAPI.getTrainFull(requestedTrainId);
      const fullTrain = extractFullTrainData(response);
      if (!fullTrain?.train_id) return null;

      const members = Array.isArray(fullTrain.equipment_members)
        ? fullTrain.equipment_members.map(formatTrainMember)
        : await loadMembers(fullTrain.train_id, true);

      setMembersCache((prev) => ({ ...prev, [fullTrain.train_id]: members }));
      setTrains((prev) => {
        const nextTrain = { ...fullTrain, status: normalizeStatus(fullTrain.status) };
        const existingIndex = prev.findIndex((t) => String(t.train_id) === String(fullTrain.train_id));
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...nextTrain };
          return updated;
        }
        return [nextTrain, ...prev];
      });

      return {
        train: { ...fullTrain, status: normalizeStatus(fullTrain.status) },
        members
      };
    } catch (error) {
      toast.error(normalizeLoadErrorMessage(error, 'Failed to load equipment train'));
      return null;
    }
  };

  useEffect(() => {
    if (!didInitialLoad.current) {
      didInitialLoad.current = true;
      loadData();
    }
  }, []);

  useEffect(() => {
    const sync = () => {
      setProductionLineFilter(localStorage.getItem('activeLine') || 'All');
      setActivePlantFilter(localStorage.getItem('activePlant') || 'All');
    };
    window.addEventListener('site-context-changed', sync);
    return () => window.removeEventListener('site-context-changed', sync);
  }, []);

  useEffect(() => {
    if (location.state?.openEditTrainId && !showTrainModal) {
      const requestedTrainId = location.state.openEditTrainId;
      (async () => {
        const resolved = await resolveTrainForRoute(requestedTrainId);
        if (!resolved?.train) return;
        const { train, members } = resolved;
        setEditingTrainId(train.train_id);
        setEquipmentSearchTerms({});
        setActiveDropdownIndex(null);
        setTrainFormData({
          train_code: train.train_code || '',
          train_name: train.train_name || '',
          description: train.description || '',
          notes: train.notes || '',
          status: normalizeStatus(train.status),
          attachedFiles: parseFiles(train.attached_files || train.attachedFiles),
          members: buildTrainFormMembers(members)
        });
        setShowTrainModal(true);
        navigate(location.pathname, { replace: true, state: {} });
      })();
      return;
    }

    if (location.state?.openViewTrainId && !showViewModal) {
      const requestedTrainId = location.state.openViewTrainId;
      (async () => {
        const resolved = await resolveTrainForRoute(requestedTrainId);
        if (!resolved?.train) return;
        const { train, members } = resolved;
        setViewTrain({
          ...train,
          members,
          attachedFiles: parseFiles(train.attached_files || train.attachedFiles)
        });
        setShowViewModal(true);
        navigate(location.pathname, { replace: true, state: {} });
      })();
      return;
    }

    const queryRequestsAddTrain = new URLSearchParams(location.search).get('openAddTrain') === '1';
    if ((location.state?.openAddTrainModal || queryRequestsAddTrain) && !showTrainModal) {
      resetForm();
      setShowTrainModal(true);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
  }, [location.state, location.search, showTrainModal, showViewModal, trains, navigate, location.pathname]);

  const trainNameOptions = useMemo(() => {
    const names = trains.map((t) => t.train_name).filter(Boolean);
    return ['All', ...Array.from(new Set(names)).sort()];
  }, [trains]);

  const productionLineOptions = useMemo(() => {
    const memberEquipmentIds = new Set();
    Object.values(membersCache).forEach(members => {
      members.forEach(m => memberEquipmentIds.add(String(m.equipment_id)));
    });
    const lines = equipmentList
      .filter(eq => {
        const isInTrain = memberEquipmentIds.has(String(eq.equipment_id));
        const matchesPlant = matchesPlantScope(eq.unit_plant || eq.scope_plant_code, activePlantFilter);
        return isInTrain && matchesPlant;
      })
      .map(eq => eq.production_line)
      .filter(Boolean);
    return ['All', ...Array.from(new Set(lines)).sort()];
  }, [equipmentList, membersCache, activePlantFilter]);

  const filteredTrains = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = trains.filter((row) => {
      const trainOk = trainNameFilter === 'All' || row.train_name === trainNameFilter;
      const searchOk = !term || [row.train_code, row.train_name, row.description, row.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));

      const plantOk = matchesPlantScope(row.unit_plant, activePlantFilter);
      const lineOk = productionLineFilter === 'All' || (() => {
        const members = membersCache[String(row.train_id)] || [];
        return members.some(m => {
          const eq = equipmentList.find(e => String(e.equipment_id) === String(m.equipment_id));
          return eq && String(eq.production_line || '').includes(productionLineFilter);
        });
      })();

      const status = normalizeStatus(row.status);
      const statusOk = statusCard === 'All'
        || (statusCard === 'Approved' && status === TRAIN_STATUS.APPROVED)
        || (statusCard === 'Pending' && status === TRAIN_STATUS.PENDING)
        || (statusCard === 'Draft' && status === TRAIN_STATUS.DRAFT);

      return trainOk && searchOk && plantOk && lineOk && statusOk;
    });

    list.sort((a, b) => {
      const aVal = a[sortBy] || '';
      const bVal = b[sortBy] || '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return list;
  }, [trains, searchTerm, trainNameFilter, statusCard, sortBy, sortOrder, productionLineFilter, membersCache, equipmentList, activePlantFilter]);

  const paginatedTrains = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTrains.slice(start, start + itemsPerPage);
  }, [filteredTrains, currentPage]);

  const filteredEquipmentListForModal = useMemo(() => {
    const term = localEquipmentSearchTerm.trim().toLowerCase();
    const activePlant = localStorage.getItem('activePlant');
    const activeLine = localStorage.getItem('activeLine');

    return equipmentList.filter((eq) => {
      const plantOk = matchesPlantScope(eq.unit_plant || eq.scope_plant_code, activePlant);
      const lineOk = !activeLine || activeLine === 'All' || String(eq.production_line || eq.line_id || '').includes(activeLine);
      const searchOk = !term || String(eq.equipment_code).toLowerCase().includes(term) || String(eq.equipment_name).toLowerCase().includes(term);
      return lineOk && plantOk && searchOk;
    });
  }, [equipmentList, localEquipmentSearchTerm]);

  const equipmentMap = useMemo(() => {
    return equipmentList.reduce((acc, eq) => {
      acc[eq.equipment_id] = eq;
      return acc;
    }, {});
  }, [equipmentList]);

  const getTrainBaseArea = (train) => (
    membersCache[train.train_id]
      ? (membersCache[train.train_id] || []).reduce((acc, m) => acc + (Number(m.selected_area ?? m.total_area) || 0), 0)
      : null
  );

  const normalizeTrainIdentity = (value) => String(value || '').trim().toLowerCase();

  const buildTrainEquipmentSignature = (members = []) => members
    .filter((member) => member?.equipment_id)
    .map((member) => String(member.equipment_id))
    .sort()
    .join('|');

  const validateTrainUniqueness = async (members) => {
    const trimmedCode = String(trainFormData.train_code || '').trim();
    const trimmedName = String(trainFormData.train_name || '').trim();
    const currentTrainId = String(editingTrainId || '');

    const duplicateCode = trains.find((train) => String(train.train_id) !== currentTrainId && normalizeTrainIdentity(train.train_code) === normalizeTrainIdentity(trimmedCode));
    if (duplicateCode) return 'Equipment train code already exists';

    const duplicateName = trains.find((train) => String(train.train_id) !== currentTrainId && normalizeTrainIdentity(train.train_name) === normalizeTrainIdentity(trimmedName));
    if (duplicateName) return 'Equipment train name already exists';

    const signature = buildTrainEquipmentSignature(members);
    if (!signature) return null;

    for (const train of trains) {
      if (String(train.train_id) === currentTrainId) continue;
      const existingMembers = membersCache[train.train_id] || await loadMembers(train.train_id);
      if (buildTrainEquipmentSignature(existingMembers) === signature) {
        return `Equipment train "${train.train_name || train.train_code || train.train_id}" already uses the exact same equipment set`;
      }
    }

    return null;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTrains(paginatedTrains.map(t => t.train_id));
    } else {
      setSelectedTrains([]);
    }
  };

  const openAddTrain = () => {
    resetForm();
    setShowTrainModal(true);
  };

  const openEditTrain = async (train) => {
    if (normalizeStatus(train.status) === TRAIN_STATUS.APPROVED) {
      setPendingChangeRef(null);
      setChangeRefGate({ train });
      return;
    }
    await openEditTrainForm(train);
  };

  const confirmChangeReference = async (data) => {
    setChangeRefBusy(true);
    try {
      setPendingChangeRef(data);
      editingApprovedTrainRef.current = true;
      await openEditTrainForm(changeRefGate.train);
      setChangeRefGate(null);
    } finally {
      setChangeRefBusy(false);
    }
  };

  const openEditTrainForm = async (train) => {
    const members = await loadMembers(train.train_id, true);
    setEditingTrainId(train.train_id);
    setEquipmentSearchTerms({});
    setActiveDropdownIndex(null);
    setTrainFormData({
      train_code: train.train_code || '',
      train_name: train.train_name || '',
      production_line: train.production_line || '',
      description: train.description || '',
      notes: train.notes || '',
      status: normalizeStatus(train.status),
      attachedFiles: parseFiles(train.attached_files || train.attachedFiles),
      members: buildTrainFormMembers(members)
    });
    setShowTrainModal(true);
  };

  const openViewTrain = async (train) => {
    try {
      const response = await equipmentAPI.getTrainFull(train.train_id);
      const fullTrain = extractFullTrainData(response) || train;
      const members = Array.isArray(fullTrain.equipment_members)
        ? fullTrain.equipment_members.map(formatTrainMember)
        : await loadMembers(train.train_id, true);
      setMembersCache((prev) => ({ ...prev, [train.train_id]: members }));
      setViewTrain({
        ...train,
        ...fullTrain,
        members,
        linked_products: Array.isArray(fullTrain.linked_products) ? fullTrain.linked_products : [],
        summary: fullTrain.summary || null,
        attachedFiles: parseFiles(fullTrain.attached_files || fullTrain.attachedFiles || train.attached_files || train.attachedFiles)
      });
    } catch (error) {
      const members = await loadMembers(train.train_id, true);
      setViewTrain({
        ...train,
        members,
        linked_products: [],
        summary: null,
        attachedFiles: parseFiles(train.attached_files || train.attachedFiles)
      });
    }
    setShowViewModal(true);
  };

  const openEquipmentDetails = (equipmentId, mode = 'view') => {
    if (!equipmentId) return;
    setShowViewModal(false);
    navigate('/equipment-master', {
      state: mode === 'edit' ? { openEditEquipmentId: equipmentId } : { openViewEquipmentId: equipmentId }
    });
  };

  const openCopyTrain = async (train) => {
    const members = await loadMembers(train.train_id, true);
    const nextCode = `COPY-${train.train_code || train.train_id}`.slice(0, 95);
    setEditingTrainId(null);
    setTrainFormData({
      train_code: nextCode,
      train_name: `${train.train_name || 'Train'} (Copy)`,
      description: train.description || '',
      notes: train.notes || '',
      status: TRAIN_STATUS.DRAFT,
      attachedFiles: parseFiles(train.attached_files || train.attachedFiles),
      members: members.length > 0
        ? buildTrainFormMembers(members).map((m, idx) => ({
            ...m,
            train_member_id: undefined,
            sequence_order: idx + 1,
            attachedFiles: []
          }))
        : [{ equipment_id: '', equipment_code: '', equipment_name: '', production_line: '', sequence_order: 1, area_scope: 'All', cleaning_method: 'Manual', notes: '' }]
    });
    setShowTrainModal(true);
  };

  const handleRowClick = async (train) => {
    setSelectedTrainRow(train.train_id);
    await loadMembers(train.train_id);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const renderSortIndicator = (field) => {
    const isActive = sortBy === field;
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '6px', lineHeight: 0.75, verticalAlign: 'middle' }}>
        <span style={{ color: isActive && sortOrder === 'asc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)' }}>{'\u25B2'}</span>
        <span style={{ color: isActive && sortOrder === 'desc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)', marginTop: '-2px' }}>{'\u25BC'}</span>
      </span>
    );
  };

  const updateFormMember = (index, key, value) => {
    setTrainFormData((prev) => {
      const members = [...prev.members];
      members[index] = { ...members[index], [key]: value };
      return { ...prev, members };
    });
  };

  const addFormMemberRow = () => {
    setTrainFormData((prev) => ({
      ...prev,
      members: [...prev.members, createTrainMember({ sequence_order: prev.members.length + 1 })]
    }));
  };

  const removeFormMemberRow = (index) => {
    setTrainFormData((prev) => {
      const members = prev.members.filter((_, idx) => idx !== index);
      return {
        ...prev,
        members: members.length ? members : [createTrainMember()]
      };
    });
  };

  const saveTrainWithStatus = async (status) => {
    if (!trainFormData.train_code.trim() || !trainFormData.train_name.trim()) {
      toast.error('Train code and train name are required');
      return;
    }

    const validMembers = (trainFormData.members || [])
      .filter((m) => m.equipment_id)
      .map((m, idx) => ({
        ...m,
        sequence_order: toNum(m.sequence_order, idx + 1) || (idx + 1),
        area_scope: normalizeAreaScope(m.area_scope),
        cleaning_method: normalizeCleaningMethod(m.cleaning_method),
        usage_remark: String(m.usage_remark || '').trim(),
        validation_batch_justification: String(m.validation_batch_justification || '').trim(),
        manual_contamination_required: Boolean(m.manual_contamination_required),
        manual_contamination_notes: String(m.manual_contamination_notes || '').trim(),
        maco_value: toNullableInputNumber(m.maco_value),
        contamination_factor: toNullableInputNumber(m.contamination_factor),
        product_quantity: toNullableInputNumber(m.product_quantity),
        contamination_quantity: toNullableInputNumber(m.contamination_quantity) ?? getCalculatedContaminationQuantity(m)
      }));

    if (!validMembers.length) {
      toast.error('At least one equipment is required in train');
      return;
    }

    const uniquenessError = await validateTrainUniqueness(validMembers);
    if (uniquenessError) {
      toast.error(uniquenessError);
      return;
    }

    const formData = new FormData();
    formData.append('train_code', trainFormData.train_code.trim());
    formData.append('train_name', trainFormData.train_name.trim());
    formData.append('production_line', trainFormData.production_line || '');
    formData.append('description', trainFormData.description || '');
    formData.append('notes', trainFormData.notes || '');
    formData.append('status', status);
    formData.append('esign_password', eSignData.password);
    formData.append('esign_comment', eSignData.comment);
    if (editingTrainId && editingApprovedTrainRef.current && pendingChangeRef) {
      formData.append('change_reference_type', pendingChangeRef.reference_type);
      formData.append('change_reference_number', pendingChangeRef.reference_number);
      formData.append('change_reference_files', JSON.stringify(pendingChangeRef.reference_files || []));
    }

    // Handle files
    const existingFiles = [];
    (trainFormData.attachedFiles || []).forEach(file => {
      if (file instanceof File) {
        formData.append('attachedFiles', file, file.name);
      } else if (typeof file === 'string') {
        existingFiles.push(file);
      }
    });
    formData.append('existingAttachedFiles', JSON.stringify(existingFiles));

    try {
      setSaving(true);
      let trainId = editingTrainId;
      if (editingTrainId) {
        // Assuming updateTrain supports FormData or we need to adjust backend. 
        // If backend expects JSON for update, we might need to separate file upload.
        await equipmentAPI.updateTrain(editingTrainId, formData);
      } else {
        const created = await equipmentAPI.createTrain(formData);
        trainId = created?.data?.data?.train_id;
      }

      if (!trainId) {
        throw new Error('Unable to resolve train id');
      }

      const existing = await equipmentAPI.getTrainMembers(trainId);
      const existingMembers = Array.isArray(existing?.data?.data) ? existing.data.data : [];
      const existingIds = new Set(existingMembers.map((row) => Number(row.train_member_id)));
      const keepIds = new Set();

      for (const member of validMembers) {
        if (member.train_member_id) {
          const memberFormData = new FormData();
          memberFormData.append('sequence_order', member.sequence_order);
          memberFormData.append('area_scope', member.area_scope);
          memberFormData.append('cleaning_method', member.cleaning_method);
          memberFormData.append('notes', member.notes || '');
          memberFormData.append('usage_remark', member.usage_remark || '');
          memberFormData.append('validation_batch_justification', member.validation_batch_justification || '');
          memberFormData.append('manual_contamination_required', String(Boolean(member.manual_contamination_required)));
          memberFormData.append('manual_contamination_notes', member.manual_contamination_notes || '');
          memberFormData.append('maco_value', member.maco_value ?? '');
          memberFormData.append('contamination_factor', member.contamination_factor ?? '');
          memberFormData.append('product_quantity', member.product_quantity ?? '');
          memberFormData.append('contamination_quantity', member.contamination_quantity ?? getCalculatedContaminationQuantity(member) ?? '');

          const existingMemberFiles = [];
          (member.attachedFiles || []).forEach(file => {
            if (file instanceof File) memberFormData.append('attachedFiles', file, file.name);
            else if (typeof file === 'string') existingMemberFiles.push(file);
          });
          memberFormData.append('existingAttachedFiles', JSON.stringify(existingMemberFiles));

          await equipmentAPI.updateTrainMember(member.train_member_id, memberFormData);
          keepIds.add(Number(member.train_member_id));
        } else {
          const memberFormData = new FormData();
          memberFormData.append('equipment_id', Number(member.equipment_id));
          memberFormData.append('sequence_order', member.sequence_order);
          memberFormData.append('area_scope', member.area_scope);
          memberFormData.append('cleaning_method', member.cleaning_method);
          memberFormData.append('notes', member.notes || '');
          memberFormData.append('usage_remark', member.usage_remark || '');
          memberFormData.append('validation_batch_justification', member.validation_batch_justification || '');
          memberFormData.append('manual_contamination_required', String(Boolean(member.manual_contamination_required)));
          memberFormData.append('manual_contamination_notes', member.manual_contamination_notes || '');
          memberFormData.append('maco_value', member.maco_value ?? '');
          memberFormData.append('contamination_factor', member.contamination_factor ?? '');
          memberFormData.append('product_quantity', member.product_quantity ?? '');
          memberFormData.append('contamination_quantity', member.contamination_quantity ?? getCalculatedContaminationQuantity(member) ?? '');

          (member.attachedFiles || []).forEach(file => {
            if (file instanceof File) memberFormData.append('attachedFiles', file, file.name);
          });
          // New members don't have existing files to preserve

          const createdMember = await equipmentAPI.addTrainMember(trainId, memberFormData);
          const id = createdMember?.data?.data?.train_member_id;
          if (id) keepIds.add(Number(id));
        }
      }

      for (const oldMemberId of existingIds) {
        if (!keepIds.has(oldMemberId)) {
          await equipmentAPI.removeTrainMember(oldMemberId);
        }
      }

      toast.success(editingTrainId ? 'Train updated successfully' : 'Train created successfully');
      setShowTrainModal(false);
      setShowESignModal(false);
      setPendingAction(null);
      setESignData({ password: '', comment: '', decision: 'approve' });
      resetForm();
      if (location.state?.from) {
        navigate(location.state.from);
      } else {
        await loadData();
        await loadMembers(trainId, true);
        setSelectedTrainRow(trainId);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save train');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveTrain = (train, decision = 'approve') => {
    setSearchTerm('');
    setPendingAction({ type: 'approve', train });
    setESignData((prev) => ({ ...prev, decision }));
    setShowESignModal(true);
  };

  const handleBulkApprove = () => {
    const eligibleTrains = bulkTrainApproveEligible.map((train) => train.train_id);

    if (!eligibleTrains.length) {
      toast.info('No selected trains are eligible for approval.');
      return;
    }

    setSearchTerm('');
    setPendingAction({ type: 'bulk_approve', trainIds: eligibleTrains });
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  }; 

  const handleDeleteTrain = async (train) => {
    setSearchTerm('');
    const stage = getTrainDeleteStage(train);
    setPendingAction({ type: 'delete', train, stage });
    setTrainDeletionImpact(null);
    if (stage === 'request' || stage === 'draft') {
      try {
        const res = await equipmentAPI.getTrainDeletionImpact(train.train_id);
        setTrainDeletionImpact(res?.data?.data || null);
      } catch { /* non-blocking preview — ignore failures */ }
    }
    setShowESignModal(true);
  };

  const handleTrainDeletionApproval = (train, stage) => {
    setSearchTerm('');
    setPendingAction({ type: 'delete_approval', train, stage });
    setShowESignModal(true);
  };

  const handleBulkDeleteApproval = (stage) => {
    const eligibleTrains = (stage === 'product' ? bulkTrainDeleteProductEligible : bulkTrainDeleteQaEligible).map((train) => train.train_id);
    if (!eligibleTrains.length) {
      toast.info('No selected trains are eligible for this delete approval stage.');
      return;
    }
    setSearchTerm('');
    setPendingAction({ type: 'bulk_delete_approval', stage, trainIds: eligibleTrains });
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  // Editing an existing train with nothing changed: warn instead of asking for a signature.
  // Submitting a Draft for approval is still a real action, so that path is exempt.
  const isUnchangedEdit = (mode) => {
    if (!editingTrainId || !initialTrainRef.current) return false;
    const { json, status } = initialTrainRef.current;
    if (mode === 'submit' && status === TRAIN_STATUS.DRAFT) return false;
    return json === JSON.stringify(trainFormData);
  };

  const handleModalDraft = () => {
    if (isUnchangedEdit('draft')) return toast.info('No changes were made — nothing to save, so no signature is required.');
    setSearchTerm('');
    setPendingAction({ type: 'draft' });
    setShowESignModal(true);
  };

  const handleModalSubmit = () => {
    if (isUnchangedEdit('submit')) return toast.info('No changes were made — nothing to save, so no signature is required.');
    setSearchTerm('');
    setPendingAction({ type: 'submit' });
    setShowESignModal(true);
  };



  useEffect(() => {
    if (showESignModal) {
      setSearchTerm('');
    }
  }, [showESignModal]);

  const handleConfirmESign = async (decisionOverride) => {
    if (!eSignData.password.trim()) {
      toast.error('Password is required for this action.');
      return;
    }
    if (!eSignData.comment.trim()) {
      toast.error('Reason / Comment is required.');
      return;
    }

    try {
      setSaving(true);
      if (pendingAction?.type === 'approve' && pendingAction.train) {
        await equipmentAPI.approveTrain(pendingAction.train.train_id, {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment,
          decision: decisionOverride || eSignData.decision
        });
        toast.success('Train approved successfully');
        await loadData();
      } else if (pendingAction?.type === 'bulk_approve' && Array.isArray(pendingAction.trainIds)) {
        const response = await equipmentAPI.bulkApproveTrain(pendingAction.trainIds, {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment,
          decision: decisionOverride || eSignData.decision
        });
        const results = response?.data?.results || {};
        if (results.approved?.length) toast.success(`${results.approved.length} train(s) approved`);
        if (results.alreadyDone?.length) toast.info(`${results.alreadyDone.length} train(s) already approved`);
        if (results.skipped?.length) toast.info(`${results.skipped.length} train(s) were not eligible`);
        if (results.missing?.length) toast.error(`${results.missing.length} train(s) were not found`);
        if (!results.approved?.length && response?.data?.message) toast.info(response.data.message);
        setSelectedTrains([]);
        await loadData();
      } else if (pendingAction?.type === 'delete' && pendingAction.train) {
        const payload = {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment
        };
        if (pendingAction.stage && pendingAction.stage !== 'draft') payload.approval_stage = pendingAction.stage;
        if (pendingAction.stage === 'product' || pendingAction.stage === 'qa') payload.decision = decisionOverride || eSignData.decision;
        const response = await equipmentAPI.deleteTrain(pendingAction.train.train_id, payload);
        toast.success(response?.data?.message || 'Train deactivation processed successfully');
        await loadData();
        if (selectedTrainRow === pendingAction.train.train_id) {
          setSelectedTrainRow(null);
        }
      } else if (pendingAction?.type === 'delete_approval' && pendingAction.train) {
        const response = await equipmentAPI.deleteTrain(pendingAction.train.train_id, {
          esign_password: eSignData.password,
          esign_comment: eSignData.comment,
          approval_stage: pendingAction.stage,
          decision: decisionOverride || eSignData.decision
        });
        toast.success(response?.data?.message || 'Train deactivation approval completed');
        await loadData();
      } else if (pendingAction?.type === 'bulk_delete' && Array.isArray(pendingAction.trainIds)) {
        try {
          const response = await equipmentAPI.bulkDeleteTrain(pendingAction.trainIds, {
            esign_password: eSignData.password,
            esign_comment: eSignData.comment
          });
          if (response.data.success) {
            toast.success(response.data.message);
            setSelectedTrains([]);
            await loadData();
            if (selectedTrainRow && pendingAction.trainIds.includes(selectedTrainRow)) {
              setSelectedTrainRow(null);
            }
          } else {
            toast.error(response.data.message || 'Bulk deactivation failed');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to bulk deactivate trains');
        }
      } else if (pendingAction?.type === 'bulk_delete_approval' && Array.isArray(pendingAction.trainIds)) {
        const response = await equipmentAPI.bulkApproveTrain(pendingAction.trainIds, {
          stage: pendingAction.stage,
          approval_type: 'delete',
          esign_password: eSignData.password,
          esign_comment: eSignData.comment
        });
        const results = response?.data?.results || {};
        if (results.approved?.length) toast.success(`${results.approved.length} train(s) deactivation-approved`);
        if (results.alreadyDone?.length) toast.info(`${results.alreadyDone.length} train(s) already processed`);
        if (results.skipped?.length) toast.info(`${results.skipped.length} train(s) were not eligible`);
        if (results.missing?.length) toast.error(`${results.missing.length} train(s) were not found`);
        if (!results.approved?.length && response?.data?.message) toast.info(response.data.message);
        setSelectedTrains([]);
        await loadData();
      } else if (pendingAction?.type === 'import' && pendingAction.file) {
        await runTrainImport(pendingAction.file);
      } else if (pendingAction?.type === 'draft') {
        await saveTrainWithStatus(TRAIN_STATUS.DRAFT);
      } else if (pendingAction?.type === 'submit') {
        await saveTrainWithStatus(TRAIN_STATUS.PENDING);
      }

      if (pendingAction?.type !== 'draft' && pendingAction?.type !== 'submit') {
        setShowESignModal(false);
        setPendingAction(null);
        setESignData({ password: '', comment: '', decision: 'approve' });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to process e-sign action');
    } finally {
      setSaving(false);
    }
  };

  const getExportContextDisplay = (value, fallback) => {
    const normalized = String(value || '').trim();
    if (!normalized) return fallback;
    if (normalized.toLowerCase() === 'all') return fallback;
    return normalized;
  };

  const getExportMetadata = (recordCount, rowCount) => {
    let savedUser = null;
    try {
      const userRaw = localStorage.getItem('user');
      savedUser = userRaw ? JSON.parse(userRaw) : null;
    } catch (error) {
      savedUser = null;
    }

    const exportedAt = new Date();
    const exportedBy =
      savedUser?.username ||
      savedUser?.full_name ||
      savedUser?.name ||
      savedUser?.employee_id ||
      'Unknown User';
    const exportedByRole = savedUser?.role || 'Unknown Role';
    const systemName = 'PrimeClean';
    const systemHost = window?.location?.origin || 'Unknown Origin';
    const systemNode = window?.location?.hostname || 'Unknown Host';
    const unitName = getExportContextDisplay(localStorage.getItem('activePlant'), 'N/A');
    const productionLine = getExportContextDisplay(localStorage.getItem('activeLine'), 'All Sections');

    return {
      reportId: `TRN-EXP-${exportedAt.getTime()}`,
      exportedAt,
      exportedAtDisplay: exportedAt.toLocaleString(),
      exportedAtIso: exportedAt.toISOString(),
      exportedBy,
      exportedByRole,
      systemName,
      systemHost,
      systemNode,
      unitName,
      productionLine,
      recordCount,
      rowCount
    };
  };

  const getExportData = async () => {
    if (!filteredTrains.length) {
      toast.info('No train records to export');
      return [];
    }
    try {
      const rows = [];
      for (const train of filteredTrains) {
        const fullTrainResponse = await equipmentAPI.getTrainFull(train.train_id).catch(() => null);
        const fullTrain = extractFullTrainData(fullTrainResponse) || null;
        const members = Array.isArray(fullTrain?.equipment_members)
          ? fullTrain.equipment_members.map(formatTrainMember)
          : await loadMembers(train.train_id);
        const linkedProducts = Array.isArray(fullTrain?.linked_products) ? fullTrain.linked_products : [];
        const productCount = linkedProducts.length;
        const productLabels = linkedProducts
          .map((product) => [product.product_code, product.product_name].filter(Boolean).join(' - '))
          .filter(Boolean)
          .join(' | ');

        const trainBase = {
          'Train Code': train.train_code,
          'Train Name': train.train_name,
          Description: fullTrain?.description || train.description || '',
          'Train Notes': fullTrain?.notes || train.notes || '',
          Status: normalizeStatus(train.status),
          'Product Count': productCount,
          'Linked Products': productLabels
        };

        if (!members.length) {
          rows.push({
            ...trainBase,
            'Equipment Code': '',
            'Equipment Name': '',
            'Sequence Order': '',
            'Area Scope': '',
            'Cleaning (Cleaning Config)': '',
            'Direct Area': '',
            'Indirect Area': '',
            'Selected Area': ''
          });
          continue;
        }

        members.forEach((member) => {
          rows.push({
            ...trainBase,
            'Equipment Code': member.equipment_code || '',
            'Equipment Name': member.equipment_name || '',
            'Sequence Order': member.sequence_order,
            'Area Scope': member.area_scope,
            'Cleaning (Cleaning Config)': formatCleaningConfigSummary(member.equipment_id),
            'Direct Area': member.direct_area,
            'Indirect Area': member.indirect_area,
            'Selected Area': member.selected_area
          });
        });
      }
      return rows;
    } catch (error) {
      toast.error('Failed to export equipment trains');
      return [];
    }
  };
  const exportToExcel = async () => {
    const rows = await getExportData();
    if (!rows.length) return;

    const metadata = getExportMetadata(filteredTrains.length, rows.length);
    const metadataRows = [
      ['PrimeClean Equipment Train Export'],
      ['21 CFR Part 11 Compliance Context'],
      [],
      ['Report ID', metadata.reportId],
      ['Report Name', 'Equipment Trains'],
      ['Unit Name', metadata.unitName],
      ['Section', metadata.productionLine],
      ['Exported At', metadata.exportedAtDisplay],
      ['Exported At (ISO)', metadata.exportedAtIso],
      ['Exported By', metadata.exportedBy],
      ['Exported By Role', metadata.exportedByRole],
      ['System Name', metadata.systemName],
      ['System Node', metadata.systemNode],
      ['System Origin', metadata.systemHost],
      ['Train Count', metadata.recordCount],
      ['Train Member Rows', metadata.rowCount],
      ['Note', 'Electronic record export with trace metadata for audit review.']
    ];

    const ws = XLSX.utils.aoa_to_sheet(metadataRows);
    const tableStartRow = metadataRows.length + 2;
    XLSX.utils.sheet_add_json(ws, rows, { origin: `A${tableStartRow}`, skipHeader: false });
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: tableStartRow - 1, c: 0 },
        e: { r: tableStartRow + rows.length - 1, c: Object.keys(rows[0] || {}).length - 1 }
      })
    };
    ws['!cols'] = Object.keys(rows[0] || {}).map((key) => ({ wch: Math.min(Math.max(String(key).length + 6, 14), 40) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment Trains');
    const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
    XLSX.writeFile(wb, `equipment_trains_${fileStamp}.xlsx`);
    toast.success('Excel exported successfully');
  };

  const drawPdfGridTable = (doc, {
    headers,
    rows,
    startY,
    topMargin,
    bottomMargin,
    leftMargin,
    rightMargin,
    headerTextColor,
    bodyTextColor,
    alternateRowColor,
    fontSize,
    colWidthWeights,
    drawHeaderFooter
  }) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableWidth = pageWidth - leftMargin - rightMargin;
    const colCount = headers.length;
    const normalizedWeights = Array.isArray(colWidthWeights) && colWidthWeights.length === colCount
      ? colWidthWeights.map((weight) => {
          const parsed = Number(weight);
          return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
        })
      : Array.from({ length: colCount }, () => 1);
    const totalWeight = normalizedWeights.reduce((sum, weight) => sum + weight, 0) || colCount;
    const colWidths = normalizedWeights.map((weight) => (tableWidth * weight) / totalWeight);
    const cellPadding = 3;
    const lineHeight = Math.max(8, fontSize + 2);
    const minRowHeight = Math.max(14, lineHeight + 2);
    const headerFontSize = Math.max(fontSize + 0.6, 6.4);
    const headerLineHeight = Math.max(8, headerFontSize + 1.8);
    const originalFontSize = typeof doc.getFontSize === 'function' ? doc.getFontSize() : fontSize;
    doc.setFontSize(headerFontSize);
    const wrappedHeaderCells = headers.map((header, index) =>
      doc
        .splitTextToSize(String(header || '').trim(), Math.max(colWidths[index] - (cellPadding * 2), 10))
        .map((line) => String(line || '').trim())
        .filter(Boolean)
    );
    doc.setFontSize(originalFontSize);
    const maxHeaderLines = wrappedHeaderCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
    const headerHeight = Math.max(minRowHeight + 4, (maxHeaderLines * headerLineHeight) + (cellPadding * 2) + 2);
    let y = startY;

    const drawHeaderRow = () => {
      doc.setFillColor(255, 255, 255);
      doc.rect(leftMargin, y, tableWidth, headerHeight, 'F');
      doc.setDrawColor(180, 192, 208);
      doc.setTextColor(...headerTextColor);
      doc.setFontSize(headerFontSize);
      doc.setFont('helvetica', 'bold');
      let x = leftMargin;
      headers.forEach((_, index) => {
        doc.rect(x, y, colWidths[index], headerHeight, 'S');
        const lineArray = Array.isArray(wrappedHeaderCells[index]) && wrappedHeaderCells[index].length > 0
          ? wrappedHeaderCells[index]
          : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + headerFontSize + (lineIndex * headerLineHeight);
          if (textY <= y + headerHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });
      doc.setFont('helvetica', 'normal');
      y += headerHeight;
    };

    const addNewPageWithHeader = () => {
      doc.addPage();
      if (typeof drawHeaderFooter === 'function') drawHeaderFooter();
      y = topMargin;
      drawHeaderRow();
    };

    if (y + headerHeight > pageHeight - bottomMargin) {
      addNewPageWithHeader();
    } else {
      drawHeaderRow();
    }

    rows.forEach((row, rowIndex) => {
      const wrappedCells = row.map((cell, index) => {
        const cellText = String(cell ?? '-');
        return doc.splitTextToSize(cellText, colWidths[index] - (cellPadding * 2));
      });
      const maxLines = wrappedCells.reduce((acc, lines) => Math.max(acc, lines.length || 1), 1);
      const rowHeight = Math.max(minRowHeight, (maxLines * lineHeight) + (cellPadding * 2));

      if (y + rowHeight > pageHeight - bottomMargin) {
        addNewPageWithHeader();
      }

      if (rowIndex % 2 === 1) {
        doc.setFillColor(...alternateRowColor);
        doc.rect(leftMargin, y, tableWidth, rowHeight, 'F');
      }

      doc.setTextColor(...bodyTextColor);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);

      let x = leftMargin;
      wrappedCells.forEach((lines, index) => {
        doc.setDrawColor(203, 213, 225);
        doc.rect(x, y, colWidths[index], rowHeight);

        const lineArray = Array.isArray(lines) && lines.length > 0 ? lines : ['-'];
        lineArray.forEach((line, lineIndex) => {
          const textY = y + cellPadding + fontSize + (lineIndex * lineHeight);
          if (textY <= y + rowHeight - cellPadding) {
            doc.text(line, x + cellPadding, textY);
          }
        });
        x += colWidths[index];
      });

      y += rowHeight;
    });
  };

  const exportToPDF = async () => {
    // Header/footer from Settings → App Configuration → Reports & Documents.
    const reportSettings = withReportProfile('equipment_train', 'Equipment Train Report');
    const rows = await getExportData();
    if (!rows.length) return;

    try {
      const metadata = getExportMetadata(filteredTrains.length, rows.length);
      const [clientLogoData, primeLogoData] = await Promise.all([
        toDataUrlIfPossible(reportSettings.report_client_logo),
        toDataUrlIfPossible(reportSettings.report_primeclean_logo)
      ]);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const headerTop = 16;
      const headerRowSplit = 44;
      const headerBottom = 82;
      const leftLogoWidth = 130;
      const rightLogoWidth = 130;
      const bodyTop = headerBottom + 8;
      const footerTop = pageHeight - 42;

      const truncatePdfHeaderText = (value, maxWidth) => {
        const text = String(value || '').trim();
        if (!text) return '-';
        if (doc.getTextWidth(text) <= maxWidth) return text;
        let truncated = text;
        while (truncated.length > 0 && doc.getTextWidth(`${truncated}...`) > maxWidth) {
          truncated = truncated.slice(0, -1);
        }
        return truncated ? `${truncated}...` : '...';
      };

      const drawHeaderFooter = () => {
        const pageInfo = doc.internal.getCurrentPageInfo ? doc.internal.getCurrentPageInfo() : null;
        const pageNo = pageInfo?.pageNumber || doc.internal.getNumberOfPages();
        const totalPages = doc.internal.getNumberOfPages();

        doc.setDrawColor(80, 80, 80);
        doc.rect(20, headerTop, pageWidth - 40, headerBottom - headerTop);
        doc.line(20 + leftLogoWidth, headerTop, 20 + leftLogoWidth, headerRowSplit);
        doc.line(pageWidth - 20 - rightLogoWidth, headerTop, pageWidth - 20 - rightLogoWidth, headerRowSplit);
        doc.line(20, headerRowSplit, pageWidth - 20, headerRowSplit);

        if (clientLogoData) {
          doc.addImage(clientLogoData, 'PNG', 26, headerTop + 7, leftLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('Client Logo', 26, headerTop + 19);
        }

        if (primeLogoData) {
          doc.addImage(primeLogoData, 'PNG', pageWidth - 20 - rightLogoWidth + 6, headerTop + 7, rightLogoWidth - 12, 18);
        } else {
          doc.setFontSize(8);
          doc.text('PrimeClean Logo', pageWidth - 20 - rightLogoWidth + 8, headerTop + 19);
        }

        doc.setFontSize(8.6);
        doc.setTextColor(15, 23, 42);
        doc.text(reportSettings.report_client_name || 'Client Name', pageWidth / 2, headerTop + 16, { align: 'center' });
        doc.setFontSize(7.6);
        doc.text(unitReportAddress(reportSettings) || 'Client Address', pageWidth / 2, headerTop + 27, { align: 'center' });

        doc.setFontSize(8.5);
        doc.text(
          truncatePdfHeaderText(`Document Name: ${reportSettings.report_document_name || 'Equipment Train Report'}`, 220),
          26,
          headerRowSplit + 14
        );
        doc.text(
          truncatePdfHeaderText(`Unit Name: ${metadata.unitName}`, 210),
          pageWidth / 2,
          headerRowSplit + 14,
          { align: 'center' }
        );
        doc.text(`Effective Date: ${metadata.exportedAtDisplay}`, pageWidth - 24, headerRowSplit + 14, { align: 'right' });
        doc.text(
          truncatePdfHeaderText(`Section: ${metadata.productionLine}`, 260),
          26,
          headerRowSplit + 26
        );
        doc.text(
          truncatePdfHeaderText(`Document & Rev. No.: ${reportSettings.report_document_rev_no || '01'}`, 210),
          pageWidth / 2,
          headerRowSplit + 26,
          { align: 'center' }
        );

        // Footer
        doc.line(20, footerTop, pageWidth - 20, footerTop);
        doc.setFontSize(7.2);
        doc.text(
          `Report ID: ${metadata.reportId}  Generated By: ${metadata.exportedBy} (${metadata.exportedByRole})  System: ${metadata.systemName} | ${metadata.systemNode}`,
          pageWidth / 2,
          pageHeight - 27,
          { align: 'center' }
        );
        if (reportSettings.report_format_no_equipment_train) {
          doc.text(`Format No: ${reportSettings.report_format_no_equipment_train}`, pageWidth / 2, pageHeight - 19, { align: 'center' });
        }
        doc.setFontSize(8);
        doc.text(`Page ${pageNo} of ${totalPages}`, 24, pageHeight - 12);
        doc.text(`Print: ${metadata.exportedAtDisplay}`, pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.text(reportSettings.report_footer_tagline || 'Data is generated and handled by PrimeClean.', pageWidth - 24, pageHeight - 12, { align: 'right' });
      };

      const headers = Object.keys(rows[0] || {});
      const body = rows.map(r => headers.map(h => r[h]));

      drawHeaderFooter();
      drawPdfGridTable(doc, {
        headers,
        rows: body,
        startY: bodyTop + 4,
        topMargin: bodyTop,
        bottomMargin: 40,
        leftMargin: 20,
        rightMargin: 20,
        headerTextColor: [0, 0, 0],
        bodyTextColor: [15, 23, 42],
        alternateRowColor: [248, 250, 252],
        fontSize: 5.8,
        colWidthWeights: headers.map(() => 1),
        drawHeaderFooter
      });

      const fileStamp = metadata.exportedAt.toISOString().replace(/[:.]/g, '-');
      doc.save(`equipment_trains_${fileStamp}.pdf`);
      toast.success('PDF export completed');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  const printTrainDetails = () => {
    const content = document.getElementById('equipment-train-details-print');
    if (!content) {
      toast.error('Unable to find content to print');
      return;
    }
    
    openPrintPreview({
      reportKey: 'equipment_train',
      contentHTML: content.innerHTML,
      title: `${viewTrain?.train_name || 'Equipment Train'} Details`,
      documentName: 'Equipment Train Details',
      clientName: reportSettings?.report_client_name || 'Client Name',
      clientAddress: unitReportAddress(reportSettings) || 'Client Address',
      clientLogoUrl: reportSettings?.report_client_logo || '',
      primeCleanLogoUrl: reportSettings?.report_primeclean_logo || '',
      revisionNo: reportSettings?.report_document_rev_no || '01',
      formatNo: reportSettings?.report_format_no_equipment_train || '',
      unitName: getActivePrintUnitName(),
      timestamp: new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    });
  };
  const handleBulkDelete = async () => {
    if (!selectedTrains.length) return;
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can bulk deactivate trains');
      return;
    }
    setSearchTerm('');
    setPendingAction({ type: 'bulk_delete', trainIds: [...selectedTrains] });
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const handleLinkProductToSelectedTrain = () => {
    if (!pendingProductLink?.product_id) {
      toast.error('No product selected for train linking');
      return;
    }
    if (!selectedTrainForProductLink?.train_id) {
      toast.error('Select an equipment train first');
      return;
    }
    navigate('/product-master', {
      state: {
        openEditProductId: pendingProductLink.product_id,
        preselectTrainId: selectedTrainForProductLink.train_id
      }
    });
  };

  const handleCancelProductLink = () => {
    navigate(location.state?.from || '/product-master');
  };

  const downloadImportTemplate = () => {
    const templateRows = [
      {
        train_code: 'TRN-001',
        train_name: 'Primary Compression Train',
        description: 'Line-1 train',
        status: 'Draft',
        equipment_code: 'EQ-001',
        sequence_order: 1,
        area_scope: 'All',
        cleaning_method: 'Manual',
        member_notes: 'Optional Notes',
        train_notes: 'Optional train notes'
      },
      {
        train_code: 'TRN-001',
        train_name: 'Primary Compression Train',
        description: 'Line-1 train',
        status: 'Draft',
        equipment_code: 'EQ-002',
        sequence_order: 2,
        area_scope: 'Direct',
        cleaning_method: 'Manual',
        member_notes: 'Coating section',
        train_notes: 'Optional train notes'
      }
    ];

    const instructions = [
      ['Equipment Train Import Template (Inlined Format)'],
      [],
      ['Fill one Train Member (Equipment) per row. A Train with multiple members should have multiple rows with the SAME Train Code.'],
      ['The importer will automatically group all rows sharing the same Train Code into a single Equipment Train.'],
      ['Ensure that the Equipment Code exists in the Equipment Master before importing.'],
      ['Imported trains are saved as Drafts by default (or Pending Approval if status is set so); they can never be imported as Approved.'],
      ['Every equipment_code must exist in Equipment Master and, when a specific unit is selected, belong to that unit. A train with any unknown equipment is skipped and reported.'],
      ['Bulk import requires an electronic signature.'],
      ['Do not change header names in the Template sheet.']
    ];

    const wsTemplate = XLSX.utils.json_to_sheet(templateRows);
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);

    wsTemplate['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: templateRows.length, c: Object.keys(templateRows[0]).length - 1 }
      })
    };
    wsTemplate['!cols'] = Object.keys(templateRows[0]).map((key) => ({ wch: Math.min(Math.max(key.length + 4, 16), 32) }));
    wsInstructions['!cols'] = [{ wch: 90 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsTemplate, 'Equipment Trains');
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Read Me');
    XLSX.writeFile(wb, 'equipment_train_import_template.xlsx');
    toast.success('Import template downloaded');
  };

  const parseImportRows = (file) => {
    return new Promise((resolve, reject) => {
      const fileName = String(file?.name || '').toLowerCase();
      if (fileName.endsWith('.csv')) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => resolve(result.data || []),
          error: (err) => reject(err)
        });
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const wb = XLSX.read(e.target.result, { type: 'binary' });
            const preferredSheet =
              wb.SheetNames.find((n) => String(n).trim().toLowerCase() === 'equipment trains') ||
              wb.SheetNames.find((n) => String(n).trim().toLowerCase() === 'template') ||
              wb.SheetNames[0];
            const sheet = wb.Sheets[preferredSheet];
            const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });
            const headerIndex = grid.findIndex((cells) => cells.some((cell) =>
              String(cell || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') === 'train_code'
            ));
            if (headerIndex < 0) throw new Error('Could not find the Train Code header in this worksheet');
            const headers = grid[headerIndex].map((cell) => String(cell || '').trim());
            const rows = grid.slice(headerIndex + 1).map((cells) => headers.reduce((row, header, index) => {
              if (header) row[header] = cells[index] ?? '';
              return row;
            }, {}));
            resolve(rows);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsBinaryString(file);
      }
    });
  };

  // Import is a bulk state-changing action: collect the e-signature first, then run.
  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPendingAction({ type: 'import', file });
    setESignData({ password: '', comment: '', decision: 'approve' });
    setShowESignModal(true);
  };

  const runTrainImport = async (file) => {
    try {
      const rows = await parseImportRows(file);
      if (!rows.length) {
        toast.error('Import file has no rows');
        return;
      }

      const equipmentMap = equipmentList.reduce((acc, eq) => {
        acc[String(eq.equipment_code || '').trim().toLowerCase()] = eq;
        return acc;
      }, {});

      const activePlant = localStorage.getItem('activePlant');
      const anyPlant = !activePlant || activePlant === 'All' || activePlant === 'All Units';
      const importErrors = [];
      const grouped = {};
      rows.forEach((row) => {
        const code = String(row.train_code || row['Train Code'] || '').trim();
        const name = String(row.train_name || row['Train Name'] || '').trim();
        if (!code || !name) return;
        if (!grouped[code]) {
          grouped[code] = {
            train_code: code,
            train_name: name,
            description: String(row.description || row.Description || '').trim(),
            notes: String(row.train_notes || row.notes || row['Train Notes'] || '').trim(),
            status: normalizeStatus(row.status || row.Status || TRAIN_STATUS.DRAFT),
            members: [],
            errors: []
          };
        }
        const rawEquipmentCode = String(row.equipment_code || row['Equipment Code'] || '').trim();
        const equipment = equipmentMap[rawEquipmentCode.toLowerCase()];
        if (!equipment) {
          grouped[code].errors.push(rawEquipmentCode ? `equipment "${rawEquipmentCode}" not found in Equipment Master` : 'a row has no equipment_code');
          return;
        }
        if (!anyPlant && !String(equipment.unit_plant || '').includes(activePlant)) {
          grouped[code].errors.push(`equipment "${rawEquipmentCode}" belongs to ${equipment.unit_plant || 'no unit'}, not ${activePlant}`);
          return;
        }
        grouped[code].members.push({
          equipment_id: equipment.equipment_id,
          sequence_order: toNum(row.sequence_order || row['Sequence Order'], grouped[code].members.length + 1),
          area_scope: normalizeAreaScope(row.area_scope || row['Area Scope'] || 'All'),
          cleaning_method: normalizeCleaningMethod(row.cleaning_method || row['Cleaning Method'] || 'Manual'),
          notes: String(row.member_notes || row['Notes'] || '').trim()
        });
      });

      const entries = Object.values(grouped);
      if (!entries.length) {
        toast.error('No valid train rows found in import file');
        return;
      }

      let imported = 0;
      for (const entry of entries) {
        if (entry.errors.length) {
          importErrors.push(`${entry.train_code}: ${[...new Set(entry.errors)].join('; ')}`);
          continue;
        }
        if (!entry.members.length) {
          importErrors.push(`${entry.train_code}: no equipment rows`);
          continue;
        }
        try {
          const created = await equipmentAPI.createTrain({
            train_code: entry.train_code,
            train_name: entry.train_name,
            description: entry.description,
            notes: entry.notes,
            status: entry.status,
            esign_password: eSignData.password,
            esign_comment: eSignData.comment
          });
          const trainId = created?.data?.data?.train_id;
          if (!trainId) throw new Error('Unable to resolve train id');
          for (const member of entry.members) {
            await equipmentAPI.addTrainMember(trainId, member);
          }
          imported += 1;
        } catch (error) {
          importErrors.push(`${entry.train_code}: ${error?.response?.data?.message || error?.message || 'failed'}`);
        }
      }

      if (imported > 0) toast.success(`${imported} train(s) imported successfully`);
      if (importErrors.length) toast.error(`${importErrors.length} train(s) NOT imported - ${importErrors.join(' | ')}`, { autoClose: false });
      await loadData();
    } catch (error) {
      toast.error(error?.message || 'Import failed');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 className="page-title">Equipment Train Master</h2>
            <p className="page-subtitle">Groupings of equipment linked together as cleaning trains and mapped to products.</p>
          </div>
          <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-outline"
              title="AI-suggest logical equipment groupings for new trains"
              onClick={() => grouping.run('AI Suggest Train Grouping', () => aiAPI.suggestGrouping('equipment_train'))}
            >
              <FaLayerGroup /> AI Suggest Grouping
            </button>
            {isSuperAdmin && selectedTrains.length > 0 && (
              <button className="btn btn-danger" onClick={handleBulkDelete}>
                <FaTrash /> Deactivate Selected ({selectedTrains.length})
              </button>
            )}
            {canBulkTrainApprove && bulkTrainApproveEligible.length > 0 && (
              <button className="btn btn-success" onClick={handleBulkApprove}>
                <FaCheck /> Approve Selected ({bulkTrainApproveEligible.length})
              </button>
            )}
            {canBulkTrainApprove && bulkTrainDeleteProductEligible.length > 0 && (
              <button className="btn btn-info" onClick={() => handleBulkDeleteApproval('product')}>
                <FaCheck /> Approve Deact-P ({bulkTrainDeleteProductEligible.length})
              </button>
            )}
            {canBulkTrainApprove && bulkTrainDeleteQaEligible.length > 0 && (
              <button className="btn btn-warning" onClick={() => handleBulkDeleteApproval('qa')}>
                <FaCheck /> Approve Deact-Q ({bulkTrainDeleteQaEligible.length})
              </button>
            )}
            <button className="btn btn-success" onClick={exportToExcel}>
              <FaDownload /> Excel
            </button>
            <button className="btn btn-danger" onClick={exportToPDF}><FaDownload /> PDF</button>
            <button className="btn btn-outline" onClick={downloadImportTemplate} title="Download Import Template">
              <FaDownload /> Import Template
            </button>
            <label className="btn btn-info" style={{ margin: 0, cursor: 'pointer' }}>
              <FaUpload /> Import
              <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImportFile} style={{ display: 'none' }} />
            </label>
            <button className="btn btn-primary" onClick={openAddTrain}>
              <FaPlus /> Add Train
            </button>
          </div>

          <div className="toolbar-compact" style={{ gap: '10px' }}>
            <button className="btn btn-primary" onClick={openAddTrain}>
              <FaPlus /> Add Train
            </button>
            <ActionsMenu
              title="More actions"
              items={[
                { label: 'AI Suggest Grouping', icon: <FaLayerGroup />, onClick: () => grouping.run('AI Suggest Train Grouping', () => aiAPI.suggestGrouping('equipment_train')) },
                { label: `Deactivate Selected (${selectedTrains.length})`, icon: <FaTrash />, danger: true, show: isSuperAdmin && selectedTrains.length > 0, onClick: handleBulkDelete },
                { label: `Approve Selected (${bulkTrainApproveEligible.length})`, icon: <FaCheck />, show: canBulkTrainApprove && bulkTrainApproveEligible.length > 0, onClick: handleBulkApprove },
                { label: `Approve Deact-P (${bulkTrainDeleteProductEligible.length})`, icon: <FaCheck />, show: canBulkTrainApprove && bulkTrainDeleteProductEligible.length > 0, onClick: () => handleBulkDeleteApproval('product') },
                { label: `Approve Deact-Q (${bulkTrainDeleteQaEligible.length})`, icon: <FaCheck />, show: canBulkTrainApprove && bulkTrainDeleteQaEligible.length > 0, onClick: () => handleBulkDeleteApproval('qa') },
                { divider: true },
                { label: 'Export Excel', icon: <FaDownload />, onClick: exportToExcel },
                { label: 'Export PDF', icon: <FaDownload />, onClick: exportToPDF },
                { label: 'Import Template', icon: <FaDownload />, onClick: downloadImportTemplate },
                { label: 'Import', icon: <FaUpload />, onClick: () => mobileImportInputRef.current?.click() },
              ]}
            />
            <input ref={mobileImportInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleImportFile} style={{ display: 'none' }} />
          </div>
        </div>

        {pendingProductLink && (
          <div style={{ margin: '0 0 16px', padding: '16px 18px', borderRadius: '12px', border: '1px solid var(--info-border, #bfdbfe)', background: 'var(--info-bg, #eff6ff)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary-dark, #1d4ed8)', fontWeight: 700, marginBottom: '6px' }}>
                <FaLink />
                <span>Link Product to Existing Train</span>
              </div>
              <div style={{ color: 'var(--text-primary)', fontSize: 'var(--fs-lg)', marginBottom: '4px' }}>
                <span>{pendingProductLink.product_code || '-'}{pendingProductLink.product_name ? ' - ' : ''}{pendingProductLink.product_name || ''}</span>
              </div>
              {pendingProductLink.dosage_form ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)', marginBottom: '4px' }}>Dosage Form: {pendingProductLink.dosage_form}</div>
              ) : null}
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)', marginBottom: '4px' }}>Current Train: {pendingProductTrainLabel || 'Not linked'}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>Select a train row below, then click Link Product to Selected Train.</div>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={handleLinkProductToSelectedTrain} disabled={!selectedTrainForProductLink}>
                <FaLink /> Link Product to Selected Train
              </button>
              <button className="btn btn-outline" onClick={handleCancelProductLink}>Cancel</button>
            </div>
          </div>
        )}

        <div className="stat-card-grid">
          {[
            { key: 'All', title: 'Total Trains', value: trains.length, icon: FaLayerGroup, color: 'primary' },
            { key: 'Approved', title: 'Approved', value: trains.filter((t) => normalizeStatus(t.status) === TRAIN_STATUS.APPROVED).length, icon: FaCheck, color: 'success' },
            { key: 'Pending', title: 'Pending Approval', value: trains.filter((t) => normalizeStatus(t.status) === TRAIN_STATUS.PENDING).length, icon: FaHourglassHalf, color: 'warning' },
            { key: 'Draft', title: 'Draft', value: trains.filter((t) => normalizeStatus(t.status) === TRAIN_STATUS.DRAFT).length, icon: FaEdit, color: 'info' },
          ].map((c) => (
            <StatCard
              key={c.key} compact filterHint title={c.title} value={c.value} icon={c.icon} color={c.color}
              active={statusCard === c.key}
              onClick={() => { setStatusCard(c.key === 'All' || statusCard === c.key ? 'All' : c.key); setCurrentPage(1); }}
            />
          ))}
          <StatCard compact title="Equipment Available" value={equipmentList.length} icon={FaTools} color="info" />
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1 1 200px', minWidth: '160px', position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
            <input
              type="search"
              className="form-control"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search by train code/name..."
              style={{ paddingLeft: '35px' }}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore
              data-bitwarden-ignore
              spellCheck="false"
              autoCorrect="off"
              autoCapitalize="off"
              name="trainSearchNoAuto"
            />
          </div>
          {productionLineFilter && productionLineFilter !== 'All' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
              <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Section:</span>
              <span style={{ padding: '4px 10px', borderRadius: '10px', background: 'var(--success-color, #10b981)', color: '#fff', fontSize: 'var(--fs-md)', fontWeight: 600, whiteSpace: 'nowrap' }}>{productionLineFilter}</span>
            </div>
          )}
        </div>

        <FloatingScrollWrapper innerClassName="table-container">
          <table>
            <thead>
              <tr>
                {canUseBulkTrainActions && (
                  <th style={{ width: '40px' }}>
                    <input type="checkbox" onChange={handleSelectAll} checked={paginatedTrains.length > 0 && selectedTrains.length === paginatedTrains.length} />
                  </th>
                )}
                <th><button type="button" onClick={() => handleSort('train_code')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>Train Code {renderSortIndicator('train_code')}</button></th>
                <th><button type="button" onClick={() => handleSort('train_name')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>Train Name {renderSortIndicator('train_name')}</button></th>
                <th><button type="button" onClick={() => handleSort('status')} style={{ border: 'none', background: 'transparent', fontWeight: 600, cursor: 'pointer', padding: 0, color: 'inherit' }}>Status {renderSortIndicator('status')}</button></th>
                <th>Members</th>
                <th style={{ minWidth: '160px' }}>Products + APIs</th>
                <th style={{ whiteSpace: 'normal', lineHeight: 1.2, minWidth: '210px' }}>
                  {`Total surface area (Total + ${trainAreaCompensationPercent}% additional, cm²)`}
                </th>
                <th style={{ width: '150px', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!paginatedTrains.length ? (
                <tr><td colSpan={canUseBulkTrainActions ? 8 : 7} className="text-center">No train records found</td></tr>
              ) : paginatedTrains.map((train) => {
                return (
                <tr
                  key={train.train_id}
                  onClick={() => handleRowClick(train)}
                  onDoubleClick={() => openViewTrain(train)}
                  className={selectedTrainRow === train.train_id ? 'selected' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  {canUseBulkTrainActions && (
                    <td>
                      <input type="checkbox" checked={selectedTrains.includes(train.train_id)} onChange={(e) => { e.stopPropagation(); setSelectedTrains(prev => prev.includes(train.train_id) ? prev.filter(id => id !== train.train_id) : [...prev, train.train_id]); }} />
                    </td>
                  )}
                  <td><span className="record-link" onClick={(e) => { e.stopPropagation(); openViewTrain(train); }} title="View train">{train.train_code}</span></td>
                  <td>{train.train_name}</td>
                  <td>
                    <span className={`badge ${getTrainStatusInfo(train).badgeClass}`}>
                      {getTrainStatusInfo(train).text}
                    </span>
                  </td>
                  <td><small>{membersCache[train.train_id] ? membersCache[train.train_id].length : (train.member_count || 0)} members</small></td>
                  <td>
                    <small>
                      {train.product_count || 0} product{(train.product_count || 0) !== 1 ? 's' : ''} / {train.api_count || 0} API{(train.api_count || 0) !== 1 ? 's' : ''}
                    </small>
                  </td>
                  <td>
                    <small>
                      {membersCache[train.train_id]
                        ? `${(getTrainBaseArea(train) * (1 + ((Number(trainAreaCompensationPercent) || 0) / 100))).toFixed(2)} cm²`
                        : '-'}
                    </small>
                  </td>
                  <td className="action-cell" style={{ width: '150px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      {normalizeStatus(train.status) === TRAIN_STATUS.PENDING && !train.deletion_requested && isAdminLike && (
                        <button className="btn btn-sm btn-success" onClick={(e) => { e.stopPropagation(); handleApproveTrain(train); }} title="Approve">
                          <FaCheck />
                        </button>
                      )}
                      {train.deletion_requested && !train.deletion_product_approved && canBulkTrainApprove && (
                        <button className="btn btn-sm btn-info" onClick={(e) => { e.stopPropagation(); handleTrainDeletionApproval(train, 'product'); }} title="Approve Product Deletion">
                          Del-P
                        </button>
                      )}
                      {train.deletion_requested && train.deletion_product_approved && !train.deletion_qa_approved && canBulkTrainApprove && (
                        <button className="btn btn-sm btn-warning" onClick={(e) => { e.stopPropagation(); handleTrainDeletionApproval(train, 'qa'); }} title="Approve QA Deletion">
                          Del-Q
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); openViewTrain(train); }} title="View">
                        <FaEye />
                      </button>
                      <ActionsMenu items={[
                        { label: 'Edit', icon: <FaEdit />, onClick: () => openEditTrain(train) },
                        { label: 'Copy', icon: <FaCopy />, onClick: () => openCopyTrain(train) },
                        { label: 'Version History', icon: <FaHourglassHalf />, onClick: () => setHistoryModal(train) },
                        { label: 'Deactivate', icon: <FaTrash />, onClick: () => handleDeleteTrain(train), danger: true },
                      ]} />
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </FloatingScrollWrapper>

        <TablePagination total={filteredTrains.length} page={currentPage} pageSize={itemsPerPage} onPageChange={setCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }} itemLabel="trains" />
      </div>

      {showTrainModal && (
        <Modal
          isOpen={showTrainModal}
          size="xl"
          title={`${editingTrainId ? 'Edit' : 'Add'} Equipment Train`}
          onClose={() => setShowTrainModal(false)}
          footer={
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', width: '100%' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addFormMemberRow}
                style={{ borderStyle: 'dashed', background: 'var(--info-bg, #f0f9ff)', color: 'var(--info-color, #0284c7)', borderColor: 'var(--info-border, #bae6fd)', fontWeight: 600 }}
              >
                <FaPlus /> Add Equipment
              </button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" className="btn btn-outline" onClick={() => setShowTrainModal(false)}>Cancel</button>
                <button type="button" className="btn btn-outline" onClick={handleModalDraft}>Save as Draft</button>
                <button type="button" className="btn btn-primary" onClick={handleModalSubmit}>
                  {editingTrainId ? 'Update Train' : 'Submit for Approval'}
                </button>
              </div>
            </div>
          }
        >
            <div style={{ backgroundColor: 'var(--bg-color)' }}>
              <div style={{ background: 'var(--card-bg)', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '10px' }}>
                {(() => {
                  const tLine = localStorage.getItem('activeLine') || '';
                  const tLabel = localStorage.getItem('activeLineDosageLabel') || '';
                  return tLine && tLine !== 'All' ? (
                    <div style={{ marginBottom: '14px', padding: '8px 12px', borderRadius: '8px', background: 'color-mix(in srgb, var(--success-color, #10b981) 8%, var(--card-bg))', border: '1px solid var(--success-color, #10b981)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Section:</span>
                      <strong style={{ fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}>{tLine}</strong>
                      {tLabel && <span style={{ fontSize: 'var(--fs-sm)', padding: '2px 8px', borderRadius: '10px', background: 'var(--success-color, #10b981)', color: '#fff', fontWeight: 600 }}>{tLabel}</span>}
                    </div>
                  ) : null;
                })()}
                <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}><span style={{ width: '4px', height: '20px', background: 'var(--primary-color, #2563eb)', borderRadius: '2px' }}></span>Train Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Train Code *</label>
                    <input className="form-control" value={trainFormData.train_code} onChange={(e) => setTrainFormData((prev) => ({ ...prev, train_code: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Train Name *</label>
                    <input className="form-control" value={trainFormData.train_name} onChange={(e) => setTrainFormData((prev) => ({ ...prev, train_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontWeight: '500', color: 'var(--text-secondary)' }}>Section</label>
                    <select className="form-control" value={trainFormData.production_line || ''} onChange={(e) => setTrainFormData((prev) => ({ ...prev, production_line: e.target.value }))}>
                      <option value="">-- Select Section --</option>
                      {commercialLines.map((l) => (
                        <option key={l.line_code || l.line_name} value={l.line_name}>
                          {l.line_name}{l.route_of_admin ? ` (${l.route_of_admin})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }} />
              </div>

              <div style={{ background: 'var(--card-bg)', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
                <h4 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}><span style={{ width: '4px', height: '20px', background: 'var(--success-color, #16a34a)', borderRadius: '2px' }}></span>Train Members (Equipment Sequence)</h4>
                {(trainFormData.members || []).map((member, index) => (
                  <div key={member.train_member_id || `member-${index}`} style={{ marginBottom: '12px', padding: '12px', border: '1px solid var(--border-light)', borderRadius: '10px' }}>
                    <div className="train-member-row-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1.6fr) 90px 140px 44px', gap: '8px', alignItems: 'end' }}>
                      <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                        <label className="form-label">Equipment *</label>
                        {member.equipment_id ? (
                          <div style={{ display: 'flex', alignItems: 'center', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', padding: '0.375rem 0.75rem', height: '38px' }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--fs-base)' }}>
                              {(() => {
                                const eq = equipmentList.find((e) => String(e.equipment_id) === String(member.equipment_id));
                                const displayCode = eq?.equipment_code || member.equipment_code || '';
                                const displayName = eq?.equipment_name || member.equipment_name || '';
                                return [displayCode, displayName].filter(Boolean).join(' - ') || 'Unknown Equipment';
                              })()}
                            </span>
                            <button
                              type="button"
                              onClick={() => setTrainFormData((prev) => {
                                const members = [...prev.members];
                                members[index] = createTrainMember({ ...members[index], equipment_id: '', equipment_code: '', equipment_name: '', production_line: '' });
                                return { ...prev, members };
                              })}
                              style={{ border: 'none', background: 'transparent', color: 'var(--danger-color)', cursor: 'pointer', padding: '0 0 0 8px' }}
                            >x</button>
                          </div>
                        ) : (
                          <>
                            <input
                              type="text"
                              className="form-control"
                              autoComplete="off"
                              placeholder="Search ID or Name..."
                              value={equipmentSearchTerms[index] || ''}
                              onChange={(e) => setEquipmentSearchTerms({ ...equipmentSearchTerms, [index]: e.target.value })}
                              onFocus={() => setActiveDropdownIndex(index)}
                              onBlur={() => setTimeout(() => setActiveDropdownIndex(null), 300)}
                            />
                            {activeDropdownIndex === index && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1060, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '0.25rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxHeight: '240px', display: 'flex', flexDirection: 'column' }} onMouseDown={(e) => e.preventDefault()}>
                                <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                                  {equipmentList.length === 0 && (
                                    <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>No equipment loaded</div>
                                  )}
                                  {equipmentList.filter((eq) =>
                                    (productionLineFilter === 'All' || eq.production_line === productionLineFilter) &&
                                    (String(eq.equipment_code || '').toLowerCase().includes((equipmentSearchTerms[index] || '').toLowerCase()) ||
                                    String(eq.equipment_name || '').toLowerCase().includes((equipmentSearchTerms[index] || '').toLowerCase()))
                                  ).map((eq) => (
                                    <div
                                      key={eq.equipment_id}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setTrainFormData((prev) => {
                                          const members = [...prev.members];
                                          members[index] = {
                                            ...members[index],
                                            equipment_id: eq.equipment_id,
                                            equipment_code: eq.equipment_code || '',
                                            equipment_name: eq.equipment_name || '',
                                            production_line: eq.production_line || ''
                                          };
                                          return { ...prev, members };
                                        });
                                        setEquipmentSearchTerms({ ...equipmentSearchTerms, [index]: '' });
                                        setActiveDropdownIndex(null);
                                      }}
                                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: 'var(--fs-base)', color: 'var(--text-primary)' }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.06))'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--card-bg)'; }}
                                    >
                                      <strong>{eq.equipment_code}</strong> - {eq.equipment_name} <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>({eq.production_line})</span>
                                    </div>
                                  ))}
                                  {equipmentList.filter((eq) =>
                                    (productionLineFilter === 'All' || eq.production_line === productionLineFilter) &&
                                    (String(eq.equipment_code || '').toLowerCase().includes((equipmentSearchTerms[index] || '').toLowerCase()) ||
                                    String(eq.equipment_name || '').toLowerCase().includes((equipmentSearchTerms[index] || '').toLowerCase()))
                                  ).length === 0 && (
                                    <div style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 'var(--fs-base)' }}>
                                      No matches found.
                                    </div>
                                  )}
                                </div>
                                <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-light)', background: 'var(--card-bg)', flex: '0 0 auto' }}>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setActiveDropdownIndex(null);
                                      navigate('/equipment-master', { state: { openAddEquipmentModal: true } });
                                    }}
                                    style={{ display: 'inline-block', color: 'var(--info-text, #1e40af)', textDecoration: 'none', fontWeight: 700, background: 'var(--info-bg, #dbeafe)', border: '1px solid var(--info-border, #93c5fd)', padding: '4px 8px', borderRadius: '999px', boxShadow: '0 0 12px rgba(59,130,246,0.35)', cursor: 'pointer' }}
                                  >
                                    + Add New Equipment
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        {member.equipment_id && (() => {
                          const selectedEquipment = equipmentList.find((e) => String(e.equipment_id) === String(member.equipment_id)) || member;
                          const missingFields = getMissingEquipmentMasterFields(selectedEquipment);
                          if (!missingFields.length) return null;
                          return (
                            <div style={{ marginTop: '6px', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--danger-border, #fca5a5)', background: 'var(--danger-bg, #fef2f2)' }}>
                              <div style={{ color: 'var(--danger-text, #b91c1c)', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                                Missing in Equipment Master: {missingFields.join(', ')}.
                              </div>
                              <a href="/equipment-master" target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '5px', color: 'var(--danger-text, #b91c1c)', fontSize: 'var(--fs-sm)', fontWeight: 700, textDecoration: 'none' }}>
                                Fill missing data in Equipment Master
                              </a>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Seq *</label>
                        <input className="form-control" type="number" min="1" value={member.sequence_order} onChange={(e) => updateFormMember(index, 'sequence_order', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Area Scope</label>
                        <select className="form-control" value={member.area_scope} onChange={(e) => updateFormMember(index, 'area_scope', e.target.value)}>
                          <option value="All">All</option>
                          <option value="Direct">Direct</option>
                          <option value="Indirect">Indirect</option>
                        </select>
                      </div>
                      <button type="button" className="btn btn-danger-outline" onClick={() => removeFormMemberRow(index)} title="Remove" style={{ height: '40px', width: '40px', padding: 0 }}>
                        <FaTrash />
                      </button>
                    </div>

                    {member.equipment_id && (
                      <div style={{ marginTop: '8px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                        Cleaning: {formatCleaningConfigSummary(member.equipment_id)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
        </Modal>
      )}

      {showViewModal && viewTrain && (
        <Modal isOpen={showViewModal} size="lg" title="Equipment Train Details" onClose={() => setShowViewModal(false)}>
              <div id="equipment-train-details-print">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div><label style={{ fontWeight: 600 }}>Train Code</label><p>{viewTrain.train_code}</p></div>
                  <div><label style={{ fontWeight: 600 }}>Train Name</label><p>{viewTrain.train_name}</p></div>
                  <div><label style={{ fontWeight: 600 }}>Status</label><p>{normalizeStatus(viewTrain.status)}</p></div>
                  <div><label style={{ fontWeight: 600 }}>Member Count</label><p>{(viewTrain.members || []).length}</p></div>
                  <div><label style={{ fontWeight: 600 }}>Linked Product Count</label><p>{viewTrain.summary?.total_products ?? (viewTrain.linked_products || []).length}</p></div>
                  <div><label style={{ fontWeight: 600 }}>Total Surface Area</label><p>{Number(viewTrain.summary?.total_surface_area || 0).toFixed(2)} cm²</p></div>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>Linked Products</label>
                  {(viewTrain.linked_products || []).length === 0 ? (
                    <p className="text-muted">No products linked to this train</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                      {(viewTrain.linked_products || []).map((product) => (
                        <div key={product.product_id} style={{ padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--card-bg)' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{[product.product_code, product.product_name].filter(Boolean).join(' - ')}</div>
                          <div style={{ marginTop: '4px', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
                            Dosage Form: {product.dosage_form || '-'} | Batch Size: {product.batch_size || '-'} {product.batch_size_unit || ''} | Status: {normalizeStatus(product.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <FloatingScrollWrapper innerClassName="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Seq</th>
                        <th>Equipment</th>
                        <th>Area Scope</th>
                        <th>Cleaning</th>
                        <th>Direct</th>
                        <th>Indirect</th>
                        <th>Selected</th>
                        <th>Contamination Plan</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewTrain.members || []).length === 0 ? (
                        <tr><td colSpan={9} className="text-center">No members</td></tr>
                      ) : (viewTrain.members || []).map((m) => (
                        <tr key={m.train_member_id || `${m.equipment_id}-${m.sequence_order}`}>
                          <td>{m.sequence_order}</td>
                          <td>{(() => {
                            const linkedEquipment = equipmentList.find((eq) => String(eq.equipment_id) === String(m.equipment_id));
                            const code = m.equipment_code || linkedEquipment?.equipment_code || '-';
                            const name = m.equipment_name || linkedEquipment?.equipment_name || '-';
                            const label = `${code} - ${name}`;
                            return m.equipment_id ? (
                              <button
                                type="button"
                                onClick={() => openEquipmentDetails(m.equipment_id, 'view')}
                                style={{ border: 'none', background: 'none', padding: 0, color: 'var(--primary-color, #2563eb)', cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}
                                title="View equipment details"
                              >
                                {label}
                              </button>
                            ) : label;
                          })()}</td>
                          <td>{normalizeAreaScope(m.area_scope)}</td>
                          <td style={{ fontSize: 'var(--fs-md)' }}>{formatCleaningConfigSummary(m.equipment_id)}</td>
                          <td>{toNum(m.direct_area, 0).toFixed(2)}</td>
                          <td>{toNum(m.indirect_area, 0).toFixed(2)}</td>
                          <td>{toNum(m.selected_area, 0).toFixed(2)}</td>
                          <td style={{ fontSize: 'var(--fs-md)' }}>{formatContaminationDisplay(m) || '-'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <button className="btn btn-sm btn-info" type="button" onClick={() => openEquipmentDetails(m.equipment_id, 'view')} disabled={!m.equipment_id} title="View equipment">
                                <FaEye />
                              </button>
                              <button className="btn btn-sm btn-outline" type="button" onClick={() => openEquipmentDetails(m.equipment_id, 'edit')} disabled={!m.equipment_id} title="Edit equipment">
                                <FaEdit />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </FloatingScrollWrapper>

                <div style={{ marginTop: '20px' }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '8px' }}>Attached Files</label>
                  {(viewTrain.attachedFiles || []).length === 0 ? <p className="text-muted">No files attached</p> : (
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {(viewTrain.attachedFiles || []).map((file, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}><a href={toFileUrl(file)} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--primary-color, #2563eb)' }}><FaLink /> {file}</a></li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            <ModalFooter>
              <button className="btn btn-outline" type="button" onClick={printTrainDetails}><FaPrint style={{ marginRight: 4 }} />Print</button>
              <button className="btn btn-outline" type="button" data-module="sampling_plans" title="Worst-case and sampling-plan coverage of this train" onClick={() => navigate(`/gap-assessment?view=all&search=${encodeURIComponent(viewTrain.train_code || viewTrain.train_name || '')}`)}>Gap Assessment</button>
              <button className="btn btn-primary" onClick={() => { setShowViewModal(false); openEditTrain(viewTrain); }}>Edit</button>
              {normalizeStatus(viewTrain.status) === TRAIN_STATUS.PENDING && !viewTrain.deletion_requested && isAdminLike && (
                <button className="btn btn-success" onClick={() => { setShowViewModal(false); handleApproveTrain(viewTrain); }}>Approve / Reject</button>
              )}
            </ModalFooter>
        </Modal>
      )}
      <ChangeReferenceModal
        open={!!changeRefGate}
        qmsLinked={qmsLinked}
        busy={changeRefBusy}
        recordLabel={changeRefGate?.train?.train_name}
        onCancel={() => setChangeRefGate(null)}
        onConfirm={confirmChangeReference}
      />

      <ESignModal
        isOpen={showESignModal}
        onCancel={() => { setShowESignModal(false); setPendingAction(null); setTrainDeletionImpact(null); setESignData({ password: '', comment: '', decision: 'approve' }); }}
        message={pendingAction?.type === 'bulk_delete'
          ? `Deactivate ${pendingAction?.trainIds?.length || 0} selected train(s)?`
          : pendingAction?.type === 'delete'
            ? [
                pendingAction?.stage === 'draft'
                  ? `Deactivate draft train "${pendingAction?.train?.train_name || pendingAction?.train?.train_code || 'train'}" now?`
                  : pendingAction?.stage === 'product'
                    ? `Complete Product deactivation approval for "${pendingAction?.train?.train_name || pendingAction?.train?.train_code || 'train'}"?`
                    : pendingAction?.stage === 'qa'
                      ? `Complete QA deactivation approval for "${pendingAction?.train?.train_name || pendingAction?.train?.train_code || 'train'}"?`
                      : `Request deactivation approval for "${pendingAction?.train?.train_name || pendingAction?.train?.train_code || 'train'}"?`,
                trainDeletionImpact?.products?.length
                  ? `Impact: used by ${trainDeletionImpact.products.length} active product(s) (${trainDeletionImpact.products.slice(0, 5).map((p) => p.product_code).join(', ')}${trainDeletionImpact.products.length > 5 ? ', ...' : ''}).`
                  : null,
              ].filter(Boolean).join(' ')
          : pendingAction?.type === 'delete_approval'
            ? `${pendingAction?.stage === 'product' ? 'Product' : 'QA'} deactivation approval for "${pendingAction?.train?.train_name || pendingAction?.train?.train_code || 'train'}"?`
          : pendingAction?.type === 'bulk_delete_approval'
            ? `Bulk ${pendingAction?.stage === 'product' ? 'Product' : 'QA'} deactivation approval for ${pendingAction?.trainIds?.length || 0} train(s)?`
          : pendingAction?.type === 'bulk_approve'
            ? `Bulk approve ${pendingAction?.trainIds?.length || 0} selected train(s)?`
          : pendingAction?.type === 'approve'
            ? `Approve train "${pendingAction?.train?.train_name || pendingAction?.train?.train_code || 'train'}"?`
          : pendingAction?.type === 'import'
            ? `Bulk import trains from "${pendingAction?.file?.name || 'file'}"? Trains are created as Draft/Pending and need approval.`
          : pendingAction?.type === 'draft'
            ? `Save "${trainFormData?.train_name || trainFormData?.train_code || 'train'}" as draft?`
          : `Submit "${trainFormData?.train_name || trainFormData?.train_code || 'train'}" for approval?`}
        showDecision={!!(pendingAction?.type === 'approve' || pendingAction?.type === 'bulk_approve' || pendingAction?.type === 'delete_approval' || (pendingAction?.type === 'delete' && ['product', 'qa'].includes(pendingAction?.stage)))}
        decisionLabel={`Approval Decision *${pendingAction?.type === 'delete_approval' || pendingAction?.type === 'delete' ? ' (anything other than Approve cancels the deactivation request)' : ''}`}
        decisionOptions={APPROVAL_DECISION_OPTIONS}
        decisionValue={eSignData.decision}
        onDecisionChange={(value) => setESignData((prev) => ({ ...prev, decision: value }))}
        commentValue={eSignData.comment}
        onCommentChange={(value) => setESignData((prev) => ({ ...prev, comment: value }))}
        commentPlaceholder={
          pendingAction?.type === 'delete' || pendingAction?.type === 'bulk_delete' ? 'Reason for deactivation'
          : pendingAction?.type === 'delete_approval' || pendingAction?.type === 'bulk_delete_approval' ? 'Reason for deactivation approval'
          : pendingAction?.type === 'approve' || pendingAction?.type === 'bulk_approve' ? 'Reason for approval'
          : pendingAction?.type === 'draft' ? 'Reason for draft save'
          : 'Reason for submission'
        }
        passwordValue={eSignData.password}
        onPasswordChange={(value) => setESignData((prev) => ({ ...prev, password: value }))}
        onConfirm={() => handleConfirmESign()}
        confirmDisabled={saving}
        confirmLabel={saving ? 'Processing...'
          : (pendingAction?.type === 'approve' || pendingAction?.type === 'bulk_approve') ? 'Sign & Submit'
          : pendingAction?.type === 'bulk_delete_approval' ? 'Confirm Bulk Deactivation Approval'
          : pendingAction?.type === 'delete' || pendingAction?.type === 'bulk_delete' ? 'Confirm Deactivate'
            : pendingAction?.type === 'draft' ? 'Confirm Draft Save'
              : 'Confirm Submit'}
      />

      <AIResultPanel
        isOpen={grouping.isOpen}
        onClose={grouping.close}
        title={grouping.title}
        loading={grouping.loading}
        error={grouping.error}
        content={grouping.content}
        groups={grouping.groups}
        exportFileName="equipment-train-grouping-suggestions.pdf"
      />
      <VersionHistoryModal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={historyModal ? `Version History — ${historyModal.train_name || historyModal.train_code}` : ''}
        fetcher={() => equipmentAPI.getTrainRevisions(historyModal.train_id)}
      />
    </div>
  );
};

export default EquipmentTrain;
